<template>
    <v-container class="pt-0 px-0 mx-0" data-testid="take-appt" id="body-wrapper">
        <!-- IF OPEN TO PUBLIC -->
        <template v-if="fetchEstablishmentSetting('ENABLE_OPEN_TO_PUBLIC')">
            <v-layout v-if="getTexts[locale].presentationText && !isLoading">
                <welcome-message-box
                    :presentation-text="getTexts[locale].presentationText"
                    :selected-service="selectedService"
                />
            </v-layout>
            <v-layout v-if="$vuetify.breakpoint.mdAndUp" row wrap class="mx-2 mt-2">
                <v-flex xs12>
                    <Tarification :service="selectedService" />
                </v-flex>
            </v-layout>

            <v-layout mt-4 row wrap>
                <v-flex xs12>
                    <tz-loading :value="isLoading" :min-height="25" />
                    <!-- todo confirm the aria-level below if page structure is fixed -->
                    <span
                        tabindex="0"
                        role="heading"
                        aria-level="1"
                        :aria-label="appointmentStepsIntroductionLabel"
                        ref="topOfStepperRef"
                        id="top-of-stepper"
                    />
                    <v-stepper
                        v-show="!isLoading"
                        v-model="stepper"
                        :alt-labels="$vuetify.breakpoint.mdAndUp"
                        :vertical="$vuetify.breakpoint.smAndDown"
                        :class="{'pa-0' : $vuetify.breakpoint.xsOnly}"
                        style="border-radius: 6px;"
                        @change="onStepperInput"
                    >
                        <v-stepper-header v-show="$vuetify.breakpoint.mdAndUp" id="stepper-header">
                            <template v-for="s in steps">
                                <v-stepper-step
                                    :aria-label="stepAriaLabel(s)"
                                    role="button"
                                    :step="s.num"
                                    :editable="hasEditableStepper(stepper, s.num)"
                                    :complete="stepper > s.num"
                                    :class="{'pa-2' : $vuetify.breakpoint.xsOnly}"
                                    :key="s.num"
                                >
                                    <stepper-header
                                        :data="selectedService"
                                        :step="s"
                                        :current-step="stepper"
                                        :selected-service="selectedService"
                                        :selected-place="selectedPlace"
                                        :selected-date="selectedDate"
                                        :selected-time="apptForm.time"
                                        :data-testid="s.name"
                                    />
                                </v-stepper-step>

                                <v-divider v-if="steps.length > s.num" aria-hidden="true" :key="'divider-' + s.num" />
                            </template>
                        </v-stepper-header>

                        <v-stepper-items>
                            <template v-for="s in steps">
                                <v-stepper-step
                                    v-show="$vuetify.breakpoint.smAndDown"
                                    :step="s.num"
                                    :editable="stepper >= s.num && !isFetchingFirstAvail"
                                    :complete="stepper > s.num"
                                    :class="{'py-2' : $vuetify.breakpoint.xsOnly}"
                                    :key="s.num"
                                >
                                    <stepper-header
                                        :step="s"
                                        :current-step="stepper"
                                        :selected-service="selectedService"
                                        :selected-place="selectedPlace"
                                        :selected-date="selectedDate"
                                    />
                                </v-stepper-step>

                                <!-- Stepper dynamic service filters -->
                                <v-stepper-content
                                    v-if="s.name === 'dynamicServiceFilters'"
                                    :step="s.num"
                                    :key="'service-' + s.num"
                                >
                                    <step-dynamic-service-filters
                                        v-if="stepper === s.num"
                                        :dynamic-service-filters="getDynamicServiceFilters[0]"
                                        @nextStep="toNextStep(s.num)"
                                    />
                                </v-stepper-content>

                                <!-- Stepper du service -->
                                <v-stepper-content
                                    v-if="s.name === 'service'"
                                    :step="s.num"
                                    :key="'service-' + s.num"
                                >
                                    <step-service
                                        v-if="stepper === s.num"
                                        :loading="isFetchingFirstAvail"
                                        :step-number="s.num"
                                        @nextStep="toNextStep(s.num)"
                                        @previousStep="toPreviousStep(s.num)"
                                    />
                                </v-stepper-content>

                                <!-- Stepper des filtres de clientèle -->
                                <v-stepper-content
                                    v-if="s.name === 'serviceFilters'"
                                    :step="s.num"
                                    :key="'filter-' + s.num"
                                >
                                    <step-client-filters
                                        v-if="stepper === s.num"
                                        :answers="serviceFiltersAnswers"
                                        :selected-service="selectedService"
                                        :loading="isFetchingFirstAvail"
                                        :filter-questions="filterQuestions"
                                        @answers="onServiceFiltersAnswers"
                                        @nextStep="toNextStep(s.num)"
                                        @previousStep="toPreviousStep(s.num)"
                                    />
                                </v-stepper-content>

                                <!-- Stepper du lieu -->
                                <v-stepper-content
                                    v-if="s.name === 'place'"
                                    :step="s.num"
                                    :key="'place-' + s.num"
                                >
                                    <step-place
                                        v-if="stepper === s.num"
                                        :step-num="s.num"
                                        :current-step="stepper"
                                        :loading="isFetchingFirstAvail"
                                        :enable-zipcode-filter="fetchEstablishmentSetting('ENABLE_ZIP_CODE_FILTER')"
                                        @onPanelInput="onPlacePanelInput"
                                        @nextStep="toNextStep(s.num)"
                                        @previousStep="toPreviousStep(s.num)"
                                    />
                                </v-stepper-content>

                                <!-- Stepper du moment -->
                                <v-stepper-content
                                    v-if="s.name === 'moment'"
                                    :step="s.num"
                                    :class="{'ml-0 pl-0 pr-6' : $vuetify.breakpoint.xsOnly}"
                                    :key="'moment-' + s.num"
                                >
                                    <v-card
                                        v-if="stepper === s.num"
                                        class="mb-12">
                                        <v-card-text>
                                            <v-layout v-if="$vuetify.breakpoint.smAndDown" row wrap>
                                                <v-flex xs12>
                                                    <Tarification :service="selectedService" />
                                                </v-flex>
                                            </v-layout>
                                            <v-layout row wrap class="grey lighten-3">
                                                <template>
                                                    <v-flex xs12>
                                                        <h2
                                                            class="pt-2 px-4 subtitle-1 font-weight-bold"
                                                            role="heading"
                                                            tabindex="0"
                                                        >
                                                            {{ $t('appt.apptDateTitle') }}
                                                        </h2>
                                                        <hr aria-hidden="true" class="mb-2 mx-4" />
                                                        <!-- Calender-header-messages component -->
                                                        <calendar-header-messages
                                                            :selected-service="selectedService"
                                                            :current-est-settings="currentEstSettings"
                                                            :service-filters-answers="serviceFiltersAnswers"
                                                            :month-has-allowed-availabilities="monthHasAllowedAvailabilities"
                                                            :delay-tolerance="delayTolerance"
                                                        />
                                                    </v-flex>

                                                    <v-flex v-if="fetchEstablishmentSetting('USE_CUSTOM_TIMEZONE')" xs12 text-center>
                                                        <timezone-autocomplete
                                                            :value="clientTimeZone"
                                                            hide-label
                                                            hide-details
                                                            small-prepend
                                                            class="timezone-select pa-0 body-2"
                                                            @update:value="clientTimeZone = $event"
                                                        />
                                                    </v-flex>
                                                    <v-flex v-if="showCalendar" xs12 mt-4>
                                                        <div
                                                            tabindex="0"
                                                            aria-labelledby="calendar-title"
                                                            aria-level="2"
                                                            role="heading"
                                                            id="calendar-header">
                                                            <navigate-months-buttons
                                                                :start="start"
                                                                :month-has-allowed-availabilities="monthHasAllowedAvailabilities"
                                                                @previousMonth="previousMonth"
                                                                @nextMonth="nextMonth"
                                                            />
                                                        </div>
                                                    </v-flex>

                                                    <v-flex
                                                        v-if="isFetchingDatesAvails"
                                                        xs12>
                                                        <v-progress-linear
                                                            :aria-label="$t('accessibility.loadingGeneric')"
                                                            class="ma-0"
                                                            indeterminate />
                                                    </v-flex>

                                                    <calendar
                                                        v-if="showCalendarComponent"
                                                        :start="start"
                                                        type="month"
                                                        :locale="locale + '-ca'"
                                                        :service-has-vaccine-submodule="serviceHasVaccineSubmodule"
                                                        :service-has-pregnancy-delay-submodule="serviceHasPregnancyDelaySubmodule"
                                                        :delay-tolerance="delayTolerance"
                                                        :selected-date="selectedDate"
                                                        :calendar-title="calendarTitle"
                                                        ref="calendar"
                                                        @dayLabelClick="dateClick"
                                                        @dayClick="dayClick"
                                                        @updateStart="updateStart"
                                                    />

                                                    <!-- Day statuses legend (Kids / Pregnancy / Covid 2nd Dose Delay) -->
                                                    <v-flex
                                                        v-if="showStatusLegend"
                                                        xs12
                                                        text-right>
                                                        <v-card>
                                                            <v-card-text class="white py-1">
                                                                <v-chip
                                                                    disabled
                                                                    class="main-avails-background-kids"
                                                                >
                                                                    <span class="body-1">
                                                                        {{
                                                                            $t('appt.dayStatus.kidsAvailabilitiesInsideDelay')
                                                                        }}
                                                                    </span>
                                                                </v-chip>
                                                                <v-chip
                                                                    v-if="showToleranceChipLegend"
                                                                    disabled
                                                                    class="tolerance-avails-background"
                                                                >
                                                                    <span class="body-1">
                                                                        {{
                                                                            $t('appt.dayStatus.kidsAvailabilitiesInsideTolerance')
                                                                        }}
                                                                    </span>
                                                                </v-chip>
                                                            </v-card-text>
                                                        </v-card>
                                                    </v-flex>
                                                </template>

                                                <!-- Loading time -->
                                                <template v-if="isFetchingDayAvails">
                                                    <v-layout justify-center>
                                                        <v-flex xs12 text-center>
                                                            <v-card>
                                                                <v-card-text class="grey lighten-3 py-1">
                                                                    <v-progress-circular
                                                                        indeterminate
                                                                        color="primary"
                                                                        class="ma-2"
                                                                        :aria-label="$t('accessibility.loadingGeneric')"
                                                                    />
                                                                </v-card-text>
                                                            </v-card>
                                                        </v-flex>
                                                    </v-layout>
                                                </template>
                                                <!-- todo confirm the aria-level below if page structure is fixed -->
                                                <span
                                                    tabindex="0"
                                                    role="heading"
                                                    aria-level="2"
                                                    :aria-label="$t('appt.chooseTime')"
                                                    ref="timeSelectorAnchor"
                                                    id="time-selector-anchor" />
                                                <template v-if="!isFetchingDayAvails">
                                                    <template v-if="monthHasAllowedAvailabilities">
                                                        <template v-if="selectedDate">
                                                            <!-- Loading time -->
                                                            <template v-if="isFetchingTimeAvails">
                                                                <v-layout justify-center>
                                                                    <v-flex xs12 text-center>
                                                                        <v-card>
                                                                            <v-card-text class="grey lighten-3 py-1">
                                                                                <v-progress-circular
                                                                                    indeterminate
                                                                                    color="primary"
                                                                                    class="ma-2"
                                                                                    :aria-label="$t('accessibility.loadingGeneric')"
                                                                                />
                                                                            </v-card-text>
                                                                        </v-card>
                                                                    </v-flex>
                                                                </v-layout>
                                                            </template>

                                                            <template v-else-if="hasTimeSlots">
                                                                <v-flex
                                                                    v-if="showAvailabilitiesOfAllPlaces || (!orderByEstablishment && !orderByResource)"
                                                                    xs12>
                                                                    <v-card data-testid="timeslots">
                                                                        <v-card-text class="grey lighten-3 py-1">
                                                                            <v-layout>
                                                                                <v-flex>
                                                                                    <template v-slot:activator="{ on: prepend }">
                                                                                        <v-tooltip bottom slot="prepend">
                                                                                            <template v-slot:activator="{ on }">
                                                                                                <v-icon v-on="{...on, ...prepend}">
                                                                                                    access_time
                                                                                                </v-icon>
                                                                                                <div>{{ $t('appt.tooltips.time') }}</div>
                                                                                            </template>
                                                                                        </v-tooltip>
                                                                                    </template>
                                                                                    <h2
                                                                                        class="mr-4 subtitle-1 font-weight-bold"
                                                                                        role="heading"
                                                                                        tabindex="0"
                                                                                        id="apptTimeTitle"
                                                                                    >
                                                                                        {{ $t('appt.apptTimeTitle') }}
                                                                                        <br v-if="$vuetify.breakpoint.smAndDown" />
                                                                                        <show-timezone-code
                                                                                            :virtual-service="apptForm.clientData.virtualAccount"
                                                                                            class="font-weight-bold"
                                                                                        />
                                                                                    </h2>
                                                                                    <hr aria-hidden="true" />
                                                                                </v-flex>
                                                                            </v-layout>

                                                                            <v-layout
                                                                                v-if="timeSlots.length >= 1"
                                                                                row
                                                                                wrap
                                                                                class="mt-2">
                                                                                <v-flex
                                                                                    v-for="t in timeSlots"
                                                                                    xs6
                                                                                    sm3
                                                                                    my-1
                                                                                    class="text-center"
                                                                                    :key="t.time"
                                                                                >
                                                                                    <v-btn
                                                                                        :aria-label="getTimeDescriptionAria(t.time)"
                                                                                        text-color="white"
                                                                                        :class="apptForm.time === t.time ? 'button-cs-secondary-color' : 'appt-time-primary'"
                                                                                        :aria-checked="apptForm.time === t.time"
                                                                                        class="time-chip subheading py-1"
                                                                                        rounded
                                                                                        large
                                                                                        :data-testid="t.time"
                                                                                        data-gtm="calendar-time-click"
                                                                                        :id="(apptForm.time === t.time) ? 'selected-time-slot' : undefined"
                                                                                        @click="setApptGroupTime(t.time, t.avail.place, t.avail.resourceCount)"
                                                                                    >
                                                                                        {{ selectedService.activateMultiAppointment ? t.timeLabel : t.time }}
                                                                                    </v-btn>
                                                                                    <next-button
                                                                                        v-if="apptForm.time === t.time"
                                                                                        :classes="`screenreader-only button-secondary-color ${$vuetify.breakpoint.smAndDown ? 'small-button' : ''}`"
                                                                                        :disabled="!apptForm.time || !selectedDate"
                                                                                        :data-testid="`continue-${t.time}`"
                                                                                        :aria-label="accessibilityContinueButtonAriaLabel"
                                                                                        aria-describedby="selected-time-slot"
                                                                                        short-on-sm-and-down
                                                                                        :id="'selected-time-slot-' + t.time"
                                                                                        @nextStep="toNextStep(s.num)"
                                                                                    />
                                                                                </v-flex>
                                                                            </v-layout>
                                                                        </v-card-text>
                                                                    </v-card>
                                                                </v-flex>

                                                                <!-- Time slots loop if only all place is selected or display resource -->
                                                                <v-flex v-else-if="(orderByEstablishment || orderByResource)" xs12>
                                                                    <h2
                                                                        class="pt-2 px-4 subtitle-1 font-weight-bold"
                                                                        role="heading"
                                                                        tabindex="0"
                                                                    >
                                                                        {{ $t('appt.apptTimeTitle') }}
                                                                        <br v-if="$vuetify.breakpoint.smAndDown" />
                                                                        <show-timezone-code
                                                                            :virtual-service="apptForm.clientData.virtualAccount"
                                                                            class="font-weight-bold"
                                                                        />
                                                                    </h2>
                                                                    <hr aria-hidden="true" class="mb-2 mx-4" />
                                                                    <v-card data-testid="timeslots">
                                                                        <v-card-text
                                                                            v-for="(times, groupId) in timeSlots"
                                                                            class="grey lighten-3"
                                                                            :key="groupId">
                                                                            <v-layout>
                                                                                <v-flex>
                                                                                    <h3
                                                                                        class="subtitle-2"
                                                                                        role="heading"
                                                                                        tabindex="0"
                                                                                    >
                                                                                        <v-tooltip bottom slot="prepend">
                                                                                            <template v-slot:activator="{ on }">
                                                                                                <v-icon v-on="{...on}">
                                                                                                    access_time
                                                                                                </v-icon>
                                                                                                <div v-html="showGroupName($t('appt.tooltips.time'), groupId)" />
                                                                                            </template>
                                                                                        </v-tooltip>
                                                                                        {{ showGroupName($t('appt.apptTime'), groupId) }}
                                                                                    </h3>
                                                                                </v-flex>
                                                                            </v-layout>

                                                                            <v-layout
                                                                                v-if="times.length >= 1"
                                                                                row
                                                                                wrap
                                                                                class="mt-2">
                                                                                <v-flex
                                                                                    v-for="t in times"
                                                                                    aria-hidden="true"
                                                                                    xs6
                                                                                    sm3
                                                                                    my-1
                                                                                    class="text-center"
                                                                                    :key="t.time"
                                                                                >
                                                                                    <v-btn
                                                                                        :class="apptForm.time === t.time && apptForm.selectedGroupTime === groupId ? 'button-cs-secondary-color' : 'primary'"
                                                                                        :aria-selected="apptForm.time === t.time && apptForm.selectedGroupTime === groupId"
                                                                                        text-color="white"
                                                                                        class="time-chip subheading py-1"
                                                                                        rounded
                                                                                        large
                                                                                        :data-testid="t.time"
                                                                                        data-gtm="calendar-time-click"
                                                                                        :id="(apptForm.time === t.time && apptForm.selectedGroupTime === groupId) ? 'selected-group-time-slot' : undefined"
                                                                                        @click="setApptGroupTime(t.time, groupId, t.avail.resourceCount)"
                                                                                    >
                                                                                        {{ selectedService.activateMultiAppointment ? t.timeLabel : t.time }}
                                                                                    </v-btn>
                                                                                    <next-button
                                                                                        v-if="(apptForm.time === t.time && apptForm.selectedGroupTime === groupId)"
                                                                                        :classes="`screenreader-only button-secondary-color ${$vuetify.breakpoint.smAndDown ? 'small-button' : ''}`"
                                                                                        :disabled="!apptForm.time || !selectedDate"
                                                                                        :data-testid="`continue-${t.time}`"
                                                                                        :aria-label="accessibilityContinueButtonAriaLabel"
                                                                                        short-on-sm-and-down
                                                                                        aria-describedby="selected-group-time-slot"
                                                                                        :id="'selected-time-slot-' + t.time"
                                                                                        @nextStep="toNextStep(s.num)"
                                                                                    />
                                                                                </v-flex>
                                                                            </v-layout>
                                                                        </v-card-text>
                                                                    </v-card>
                                                                </v-flex>
                                                            </template>
                                                        </template>
                                                    </template>
                                                </template>
                                            </v-layout>
                                        </v-card-text>

                                        <v-card-actions v-if="showCalendar" class="grey lighten-3">
                                            <v-layout
                                                :row="!$vuetify.breakpoint.smAndDown"
                                                :column="$vuetify.breakpoint.smAndDown">
                                                <v-flex class="text-center xs12">
                                                    <previous-button
                                                        @previousStep="toPreviousStepCalendar(s.num)"
                                                    />
                                                    <next-button
                                                        :disabled="!apptForm.time || !selectedDate"
                                                        data-testid="take-appt-continue"
                                                        @nextStep="toNextStep(s.num)"
                                                    />
                                                </v-flex>
                                            </v-layout>
                                        </v-card-actions>
                                    </v-card>
                                </v-stepper-content>

                                <!-- Stepper des données nominatives -->
                                <v-stepper-content
                                    v-if="s.name === 'data'"
                                    :step="s.num"
                                    :class="{'ml-0 pl-0 pr-6' : $vuetify.breakpoint.xsOnly}"
                                    :key="'data-' + s.num"
                                >
                                    <div
                                        class="visually-hidden"
                                        tabindex="0"
                                        :aria-label="$t('appt.accessibility.fillFormA11yDirective')" />
                                    <!-- Banner pour CTA -->
                                    <Banner :show="shouldShowCtaBanner" dismissible @handleSignInClick="handleSignInClick" />

                                    <v-card
                                        v-if="stepper === s.num"
                                        class="mb-12">
                                        <v-form lazy-validation ref="appointmentForm" @input="updateAccessibilityRequiredFields">
                                            <v-layout row wrap>
                                                <!-- Metas zone -->
                                                <v-flex xs12>
                                                    <v-card class="">
                                                        <v-card-text class="px-12 grey lighten-3">
                                                            <v-layout
                                                                v-if="apptForm.max > 1 &&
                                                                    selectedService.activateMultiAppointment"
                                                            >
                                                                <v-flex class="pr-4">
                                                                    <v-subheader class="pl-0">
                                                                        {{ $t('form.multiApptLabel') }}
                                                                    </v-subheader>
                                                                    <v-slider
                                                                        v-model="apptForm.nbr"
                                                                        class="pl-4 pt-6"
                                                                        :max="apptForm.max"
                                                                        :min="1"
                                                                        thumb-label="always"
                                                                        always-dirty
                                                                        step="1"
                                                                        ticks="always"
                                                                        tick-size="2"
                                                                    />
                                                                </v-flex>
                                                                <v-flex shrink style="width: 60px" class="mt-12">
                                                                    <v-text-field
                                                                        v-model="apptForm.nbr"
                                                                        class="mt-0 txt-nbr"
                                                                        hide-details
                                                                        single-line
                                                                        type="number"
                                                                        :color="$vuetify.theme.primary"
                                                                        :background-color="$vuetify.theme.primary"
                                                                        min="1"
                                                                        :max="apptForm.max"
                                                                    />
                                                                </v-flex>
                                                            </v-layout>

                                                            <v-layout v-if="hasMeta()" row wrap>
                                                                <v-flex xs12>
                                                                    <!-- all metadata components -->
                                                                    <metadataForm
                                                                        :previous-appointment-client-data="apptForm.clientData"
                                                                        :metadata="getApptFormMetasReordered"
                                                                        :service-id="selectedService.id"
                                                                        @updateMetadata="updateMetadata"
                                                                        @updatePatientUuid="updatePatientUuid"
                                                                    />
                                                                </v-flex>
                                                                <v-flex v-if="isProfileUserNewPatient" xs12>
                                                                    <v-layout>
                                                                        <v-flex xs12>
                                                                            <v-checkbox
                                                                                v-model="delegatedPiConsent"
                                                                                :label="$t('form.delegatedPiConsent')"
                                                                                class="body-2 mt-0 mb-6"
                                                                                :rules="[
                                                                                    requiredField()
                                                                                ]"
                                                                                name="delegatedPiConsent"
                                                                            />
                                                                        </v-flex>
                                                                    </v-layout>
                                                                </v-flex>

                                                                <!--  -->
                                                                <v-flex v-if="hasMetaIn('cellphone')" xs12>
                                                                    <v-layout>
                                                                        <v-flex xs12>
                                                                            <v-checkbox
                                                                                v-model="sendConfirmationSms"
                                                                                class="body-2 mt-0 mb-6"
                                                                                hide-details
                                                                                name="sendConfirmationSms"
                                                                                :label="$t('form.receiveSMSConfirmation')" />
                                                                        </v-flex>
                                                                    </v-layout>
                                                                </v-flex>
                                                                <!-- Terms of Service and GDRP agreements -->
                                                                <v-flex xs12>
                                                                    <v-layout>
                                                                        <v-flex xs12>
                                                                            <terms-and-conditions
                                                                                :is-owner="isOwnerAppointment()"
                                                                                :value="tosAndGDPRAgreement"
                                                                                @handleTermsAndConditionsCheckbox="handleTermsAndConditionsCheckbox" />
                                                                        </v-flex>
                                                                    </v-layout>
                                                                </v-flex>
                                                                <v-flex xs12>
                                                                    <v-layout>
                                                                        <v-flex xs12>
                                                                            <v-checkbox
                                                                                v-model="consentCollectData"
                                                                                class="body-2 mt-0 mb-6"
                                                                                hide-details
                                                                                name="consentCollectData"
                                                                            >
                                                                                <template v-slot:label>
                                                                                    <div
                                                                                        @click.stop
                                                                                        v-html="isOwnerAppointment() ? $t('form.ownerConsentCollectData', [privacyPolicyLink]) : $t('form.otherConsentCollectData', [privacyPolicyLink])"
                                                                                    />
                                                                                </template>
                                                                            </v-checkbox>
                                                                        </v-flex>
                                                                    </v-layout>
                                                                </v-flex>
                                                                <!-- Optin New Service List -->
                                                                <v-flex v-if="showOptInList()" xs12>
                                                                    <v-layout>
                                                                        <v-flex xs12>
                                                                            <v-checkbox
                                                                                v-model="optinNewServiceList"
                                                                                class="body-2 mt-0 mb-6"
                                                                                hide-details
                                                                                name="optinNewServiceList"
                                                                            >
                                                                                <template v-slot:label>
                                                                                    <div v-html="isOwnerAppointment() ? $t('form.ownerConsentReceiveMarketing') : $t('form.otherConsentReceiveMarketing')" />
                                                                                </template>
                                                                            </v-checkbox>
                                                                        </v-flex>
                                                                    </v-layout>
                                                                </v-flex>
                                                            </v-layout>

                                                            <v-layout v-else row>
                                                                <v-flex xs12>
                                                                    {{ $t('appt.noMetaToDisplay') }}
                                                                </v-flex>
                                                            </v-layout>

                                                            <v-layout v-if="!isIpWhitelisted">
                                                                <v-flex xs12>
                                                                    <TrimozRecaptcha
                                                                        ref="bookApptRecaptcha"
                                                                        id="takeAppt"
                                                                        @onCaptchaAcquired="recaptchaToken = $event"
                                                                        @onCaptchaExpired="onCaptchaExpired"
                                                                    />
                                                                </v-flex>
                                                            </v-layout>
                                                        </v-card-text>
                                                    </v-card>
                                                </v-flex>
                                            </v-layout>
                                        </v-form>

                                        <v-card-actions class="grey lighten-3">
                                            <v-layout class="row">
                                                <accessibility-required-fields
                                                    v-if="showAccessibilityRequiredFields"
                                                    :form-inputs="formInputs"
                                                    :recaptcha-token="isIpWhitelisted ? true : recaptchaToken"
                                                    :terms-conditions="tosAndGDPRAgreement"
                                                    :consent-collect-data="consentCollectData"
                                                    class="row xs12"
                                                    tabindex="0"
                                                    id="accessibilityRequiredFields"
                                                />
                                                <v-flex class="text-center xs12">
                                                    <div data-gtm="appt-form-button" :data-gtm-disabled="disabledSubmit">
                                                        <previous-button
                                                            @previousStep="toPreviousStep(s.num)"
                                                        />
                                                        <next-button
                                                            :classes="btnSubmitClass"
                                                            :aria-disabled="disabledSubmit"
                                                            :aria-label="$t('base.btnSubmitAriaLabel')"
                                                            :text="$t('base.btnSubmit')"
                                                            @nextStep="formSubmit"
                                                        />
                                                    </div>
                                                </v-flex>
                                            </v-layout>
                                        </v-card-actions>
                                    </v-card>
                                </v-stepper-content>

                                <!-- Stepper de collecte d'informations Pharmacies loi 31/41 -->
                                <v-stepper-content
                                    v-if="s.name === 'dataCollectionForm'"
                                    :step="s.num"
                                    :class="`${$vuetify.breakpoint.smAndDown ? 'data-collection-form-mobile-app': ''}`"
                                    :key="'dataCollectionForm-' + s.num"
                                >
                                    <step-data-collection-form
                                        v-if="stepper === s.num"
                                        @nextStep="showCoupon()"
                                    />
                                </v-stepper-content>
                            </template>
                        </v-stepper-items>
                    </v-stepper>
                </v-flex>
                <v-flex md2 class="hidden-sm-and-down" />
            </v-layout>

            <v-layout row justify-center>
                <v-dialog v-model="dialog" scrollable max-width="1024px">
                    <v-card class="popup-text">
                        <v-divider aria-hidden="true" />
                        <v-card-text class="ql-editor" style="min-height: 100px;" v-html="popUp.text" />
                        <v-card-actions>
                            <v-spacer />
                            <v-btn color="button-secondary-color" rounded @click="dialog = false">
                                {{ $t('base.close') }}
                            </v-btn>
                        </v-card-actions>
                    </v-card>
                </v-dialog>
            </v-layout>
        </template>

        <!-- IF NOT OPEN TO PUBLIC -->
        <template v-else>
            <v-layout>
                <v-flex mx-12 mt-6 text-center>
                    <v-card class="deactivated-text deactivated-text-card">
                        <v-card-text class="text ql-editor">
                            <span v-html="getTexts[locale].publicSiteDeactivatedText" />
                        </v-card-text>
                    </v-card>
                </v-flex>
            </v-layout>
        </template>

        <closest-avails-modal
            :show="closestAvailsModal"
            :target="appointmentDataForm.dateStart"
            @gotoCalendar="gotoCalendarStep"
            @selectedTimeSlot="createAppointmentWithNewTimeslot"
            @close="closestAvailsModal = false" />
        <error-modal
            :show="errorModal"
            :error-key="errorKey"
            :replacements="errorReplacements"
            @close="errorModal = false" />
    </v-container>
</template>

<script lang="js">
import {
    addDays, addMonths, addMinutes, endOfMonth,
    format, getTime, isAfter, isBefore, parse, subDays, subMonths
} from 'date-fns';
import frLocale from 'date-fns/locale/fr';
import { parseFromTimeZone, formatToTimeZone } from 'date-fns-timezone';
import {
    assign, camelCase, cloneDeep, compact, drop, filter, find, findIndex, forEach,
    flatMap, get, groupBy, includes, indexOf, isNil, keys, map, maxBy, reduce,
    size, some, sortBy, split, uniqBy, upperFirst, values, remove,
    toUpper, isObject, isArray, startsWith, replace, isEmpty
} from 'lodash';
import humps from 'lodash-humps';
import { mapActions, mapGetters, mapMutations } from 'vuex';
import Vue from 'vue';
import { scroller } from 'vue-scrollto/src/scrollTo';
import {
    dateTimeToString,
    isUsingKc,
    authModule,
    avoidNoAppointmentFocus,
    setFocusForAccessibility,
    validateNamAgainstBirthday,
    validateMinAge
} from '@/utils';
import StepDynamicServiceFilters from '@/components/steppers/DynamicServiceFilters';
import StepDataCollectionForm from '@/components/steppers/DataCollectionForm/DataCollectionForm';
import StepService from '@/components/steppers/Service';
import StepClientFilters from '@/components/steppers/ClientFilters';
import StepPlace from '@/components/steppers/Place/Place';
import TimezoneAutocomplete from '@/components/timezones/TimezoneAutocomplete';
import TzLoading from '@/components/TzLoading';
import CalendarHeaderMessages from '@/components/steppers/CalendarHeaderMessages';
import metadataForm from '@/components/metadata/MetadataForm';
import {
    EstablishmentSetting,
    ServiceSettings,
    ConfigurationMessageEnum,
    MessageTypeEnum,
    MetadataEnum,
    SubModulesEnum,
    GenericVaccineServiceTemplateIds,
    ServiceTemplateEnum
} from '@/enums';
import { formRules } from '@/mixins/form-rules';
import { errorHandlingMixin, TrimozError } from '@trimoz/trimoz-vue-error-handling';
import ErrorModal from '@/components/ErrorModal';
import ClosestAvailsModal from '@/components/modal/ClosestAvailsModal';
import TrimozRecaptcha from '@/components/TrimozRecaptcha';
import WelcomeMessageBox from '@/components/WelcomeMessageBox';
import StepperHeader from '@/components/steppers/StepperHeader';
import Tarification from '@/components/steppers/Tarification/Tarification';
import Calendar from '@/components/calendar/Calendar';
import ShowTimezoneCode from '@/components/timezones/ShowTimezoneCode';
import Banner from '@/components/banner/Banner';
import TermsAndConditions from '@/components/termsAndConditions/TermsAndConditions';
import { headerTextMap } from '../components/steppers/StepperHeader.vue';
import { normalizeFormData } from '@/helpers/normalize-form-data/normalize-form-data';
import NavigateMonthsButtons from '@/components/calendar/navigateMonthsButtons/NavigateMonthsButtons';
import AccessibilityRequiredFields from '@/components/accessibilityRequiredFields/AccessibilityRequiredFields';
import { analyticsEventHandler } from '@/mixins/ga-event-handler';
import { inferConsentDomain } from '../helpers/env';
import PreviousButton from '@/components/buttons/PreviousButton.vue';
import NextButton from '@/components/buttons/NextButton.vue';

export default {
    name: 'take-appt',
    components: {
        Tarification,
        StepperHeader,
        WelcomeMessageBox,
        ErrorModal,
        StepDynamicServiceFilters,
        StepDataCollectionForm,
        StepService,
        StepClientFilters,
        StepPlace,
        TimezoneAutocomplete,
        TzLoading,
        CalendarHeaderMessages,
        metadataForm,
        ClosestAvailsModal,
        TrimozRecaptcha,
        Calendar,
        ShowTimezoneCode,
        Banner,
        TermsAndConditions,
        NavigateMonthsButtons,
        AccessibilityRequiredFields,
        PreviousButton,
        NextButton
    },
    mixins: [formRules, errorHandlingMixin, analyticsEventHandler],
    data() {
        return {
            MetadataEnum: MetadataEnum,
            optinNewServiceList: false,
            delegatedPiConsent: false,
            consentCollectData: false,

            // v-calendar
            start: '2019-01-01',

            // timezone
            clientTimeZone: '',

            // Panels / Selected members
            stepper: 1,
            selectedPlace: {},
            selectedDate: null,
            selectedRadioGroupTime: {},
            tosAndGDPRAgreement: false,
            submitting: false,
            appointmentCreated: false,

            // Time slots
            timeSlots: [],
            orderByEstablishment: false,
            orderByResource: false,

            // Loading states
            isFetchingFirstAvail: false,
            isFetchingDayAvails: false,
            isFetchingDatesAvails: false,
            isFetchingTimeAvails: false,

            // Pop-up
            popUp: {},
            dialog: false,

            // Patient
            patientUuid: null,

            // Appointment
            apptForm: {
                time: null,
                selectedGroupTime: '',
                metas: {},
                clientData: {
                    requester_link: 0,
                },
                nbr: 1,
                max: 1,
                sendByEmail: true,
                sendBySMS: true,
            },

            // Steps
            arrSteps: [],
            steps: [],

            // Service filters answers
            serviceFiltersAnswers: [],

            // Availabilities
            showAvailabilitiesOfAllPlaces: false,
            initiallyNoAvails: true,

            // birthdayAnswer & pregnancyDpa: for delay calculation
            pregnancyDpa: undefined,
            birthdayAnswer: undefined,
            dynamicMetasResponses: [],

            // Result for delay/tolerance
            delayTolerance: {
                minDelay: undefined,
                maxDelay: undefined,
                maxDelayInDays: undefined,
                minTolerance: undefined,
                maxTolerance: undefined,
            },
            filterQuestions: {},
            closestAvailsModal: false,
            appointmentDataForm: {},

            errorKey: null,
            errorModal: false,
            errorReplacements: {},
            recaptchaToken: null,

            // State to control checkbox value to send confirmation sms
            sendConfirmationSms: false,
            formInputs: [],
            createdAppointmentData: {},
            isFormValid: false,
        };
    },
    async created() {
        this.setApiSecurityWhitelistingIp();
        if (isNil(this.getNextAppointmentInfo)) {
            this.popUpText();
        }

        this.arrSteps = ['service', 'place', 'moment', 'data'];
        if (this.getEstablishmentSettingById(EstablishmentSetting.HIDE_CALENDARS_PUBLIC_PAGE)) {
            this.arrSteps = ['service', 'moment', 'data'];
            this.selectedPlace = { id: 0 };
            this.orderByEstablishment = true;
        }
        // Adding Dynamic service questions step if conditions are met
        if (this.serviceHasDynamicFilter) {
            this.arrSteps.unshift('dynamicServiceFilters');
        }

        this.mapSteps();
        this.clientTimeZone = this.getEstablishmentSettingById(EstablishmentSetting.DEFAULT_TIMEZONE);

        if (this.$route.query.portalPlace && this.$route.query.portalPostalCode) {
            this.$store.commit('places/SET_POSTAL_CODE_IS_VALID', true);
            this.$store.commit('places/SET_POSTAL_CODE', this.$route.query.portalPostalCode);
            this.$store.commit('places/SET_POSTAL_CODE_FROM_PORTAL', true);

            this.$store.commit('places/SET_SELECTED_PLACE_ID', parseInt(this.$route.query.portalPlace));
            this.$store.commit('places/SET_IS_SELECTED_PLACE', true);
        }

        if (this.getServices.length === 1) {
            if (this.getServiceStep && this.getServiceStep.num === 1) {
                this.checkForEstSettingsChange(this.currentEstSettings);
                await this.toNextStep(this.getServiceStep.num);
            }
        }

        /* Book another appointment */
        if (!isNil(this.getNextAppointmentInfo) && !isNil(this.getNextAppointmentInfo.secondAppointment)) {
            await this.bookAnotherAppointment();
        }

        if (this.getPortalEst && this.getServiceStep.num === 1 && isNil(this.getNextAppointmentInfo)) {
            // Auto select service have no service dynamic filters and not in book another appointment state
            await this.prepareToGoToMomentStep();
        }

        this.incrementPublicVisits();
    },
    updated() {
        avoidNoAppointmentFocus();
    },
    watch: {

        getLoggedUser(newVal) {
            if (!newVal) {
                if (get(this.$refs, 'appointmentForm[0]')) {
                    this.$refs.appointmentForm[0].reset();
                }
                this.optinNewServiceList = false;
                this.patientUuid = null;
            }
        },

        locale(newLocale, oldLocale) {
            // show popup
            this.popUpText();
            if (!isNil(this.$refs.appointmentForm) && this.$refs.appointmentForm[0]) {
                this.$refs.appointmentForm[0].resetValidation();
            }
            if (!newLocale || !oldLocale) return;
            // reset recaptcha on lg change to render translated component
            if (newLocale !== oldLocale) {
                this.onCaptchaExpired();
            }
        },
        clientTimeZone(val) {
            if (this.getEstablishmentSettingById(EstablishmentSetting.USE_CUSTOM_TIMEZONE) && this.selectedService.id && this.selectedPlace) {
                this.fetchAvailabilitiesBetweenDates();
                this.resetDatePicker();
            }
        },
        birthdayAnswer(val) {
            if (val) {
                this.calculateKidsDelayTolerance();
            }
        },
        pregnancyDpa(val) {
            if (val) {
                this.calculatePregnancyDelayTolerance();
            }
        },
        dynamicMetasResponses(metas) {
            /* build the client metas datas receive from dynamic services components */
            forEach(metas, (meta, key) => {
                this.apptForm.clientData[meta.associatedMetadata] = meta.value;
                if (meta.associatedMetadata === 'birthday') {
                    this.birthdayAnswer = meta.value;
                }
                else if (meta.associatedMetadata === 'pregnancy_dpa') {
                    this.pregnancyDpa = meta.value;
                }
            });
        },
        currentEstSettings(settings) {
            this.checkForEstSettingsChange(settings);
        },

        /**
     * Called each time value of selectedService changes
     * @param service
     */
        async selectedService(service) {
            if (!service) return;

            // Reset timeslots since they are tied to currently selected service
            this.timeSlots = [];
        },

        /**
     * Reset the currently selected service if returning to service step from another step
     * - This is required to show the establishment message in the header when returning to service step
     *   because if a service is selected, the service description is displayed in the header
     * @param step
     * @param oldStep
     * @returns {Promise<void>}
     */
        async currentStep(step, oldStep) {
            if (step.name === 'dynamicServiceFilters' && oldStep) {
                // Reset the services list when we are stepping back
                await this.getInstantiateServices();
            }
            if (oldStep && oldStep.name !== 'service' && step.name === 'service') {
                this.selectedService = null;
                await this.checkForDataCollectionFormStep();
            }
        },
    },
    computed: {
        ...mapGetters({
            locale: 'locale',
            upperLocale: 'upperLocale',
            getDynamicServiceFilters: 'services/getDynamicServiceFilters',
            serviceHasDynamicFilter: 'services/serviceHasDynamicFilter',
            getDynamicFilterMetadataValues: 'services/getDynamicFilterMetadataValues',
            getTexts: 'texts/getTexts',
            getServices: 'services/getActivatedServices',
            getEstablishment: 'establishment/getEstablishment',
            getEstablishmentSettingById: 'establishment/getEstablishmentSettingById',
            getPlaces: 'places/getPlaces',
            getSelectedPlaceId: 'places/getSelectedPlaceId',
            placesOrderByDistance: 'places/getPlacesOrderByDistance',
            getAvailabilities: 'availabilities/getAvailabilities',
            getDailyAvailabilities: 'availabilities/getDailyAvailabilities',
            getBaseMetadataById: 'services/getBaseMetadataById',
            getOrderredCopySelectedServiceMetadataValuesOverrideApplied: 'services/getOrderredCopySelectedServiceMetadataValuesOverrideApplied',
            getCopySelectedServiceMetadataValuesOverrideApplied: 'services/getCopySelectedServiceMetadataValuesOverrideApplied',
            currentEstSettings: 'services/getCurrentEstSettings',
            getSelectedServiceId: 'services/getSelectedServiceId',
            selectedService: 'services/getSelectedService',
            isLoading: 'loading/isLoading',
            extractTimeSlots: 'availabilities/extractTimeSlots',
            getNextAppointmentInfo: 'appointments/getNextAppointmentInfo',
            isIpWhitelisted: 'isIpWhitelisted',
            getPortalEst: 'services/getPortalEst',
            showCalendar: 'calendarDisplaySettings/showCalendar',
            isDateCompleteEvent: 'availabilities/isDateCompleteEvent',
            isDatePastEvent: 'availabilities/isDatePastEvent',
            isDateUpcomingEvent: 'availabilities/isDateUpcomingEvent',
            getLoggedUser: `${authModule}/getLoggedUser`,
            getPatientDataToFillMetadataForm: 'metadataForm/getPatientDataToFillMetadataForm',
            getIsOwnerRegisterComplete: `${authModule}/getIsOwnerRegisterComplete`,
            getOrderredCopySelectedServiceMetadataValuesOverrideAppliedKeys: 'services/getOrderredCopySelectedServiceMetadataValuesOverrideAppliedKeys',
            getServiceHasDataCollectionFormId: 'services/getServiceHasDataCollectionFormId',
        }),
        privacyPolicyLink() {
            return '//' + inferConsentDomain() + this.$t('form.policyLink');
        },
        appointmentStepsIntroductionLabel() {
            let serviceName = this.isServiceSelected ? this.$t('appt.accessibility.stepsIntroduction.serviceName', { serviceName: this.selectedServiceLocalName }) : '';
            let formattedPlaceAddress = this.isPlaceSelected ? this.$t('appt.accessibility.stepsIntroduction.formattedPlaceAddress', { formattedPlaceAddress: this.selectedPlace.formattedAddress }) : '';

            return this.$t('appt.accessibility.stepsIntroduction.default',
                {
                    establishmentName: this.getEstablishment?.name ?? '',
                    establishmentType: this.establishmentTypeName,
                    serviceName,
                    formattedPlaceAddress,
                    selectedPlaceName: this.isPlaceSelected ? this.selectedPlaceLocalName : '',
                });
        },
        isServiceSelected() {
            return !isNil(this.selectedService) && this.selectedService !== '';
        },
        isPlaceSelected() {
            return !isNil(this.selectedPlace) && !isEmpty(this.selectedPlace);
        },
        selectedPlaceLocalName: {
            get: function() {
                return this.selectedPlace['name' + upperFirst(this.locale)];
            },
        },
        establishmentTypeName() {
            return this.$t(`appt.establishment.type.${this.getEstablishmentSettingById(EstablishmentSetting.ESTABLISHMENT_TYPE)}`);
        },
        isProfileUserNewPatient() {
            return get(this.getPatientDataToFillMetadataForm, 'uuid') < 0;
        },
        calendarTitle: {
            get: function() {
                return upperFirst(format(this.start, 'MMMM YYYY', { locale: this.locale === 'fr' ? frLocale : null }));
            },
        },
        serviceHasVaccineSubmodule: {
            get: function() {
                return includes(find(this.currentEstSettings, ['setting', ServiceSettings.SUBMODULES]).value, SubModulesEnum.VACCINE);
            },
        },
        serviceHasPregnancyDelaySubmodule: {
            get: function() {
                return includes(find(this.currentEstSettings, ['setting', ServiceSettings.SUBMODULES]).value, SubModulesEnum.PREGNANCY_DELAY);
            },
        },
        selectedService: {
            get: function() {
                return this.$store.getters['services/getSelectedService'];
            },
            set: function(value) {
                this.$store.commit('services/SET_SELECTED_SERVICE', value);
            },
        },
        selectedServiceLocalName: {
            get: function() {
                return this.selectedService['name' + upperFirst(this.locale)];
            },
        },
        getApptFormMetasReordered: {
            get: function() {
                // Get an array of all present meta keys
                const baseKeys = this.getOrderredCopySelectedServiceMetadataValuesOverrideAppliedKeys;
                let newOrderedArray = [];

                /**
                 * For a performance gain of around +(5.5*10^5)% ops/sec (see performance enhancement here: https://gitlab.com/trimoz/front-end/clients/public/-/merge_requests/684#note_1411229413),
                 * it has been decided to use a Map with _.has(), _.get() functions approach instead of an Array with _.find().
                 */
                const transformedApptFormMetas = new Map();
                forEach(this.apptForm.metas, item => {
                    const metadataValue = item.metadata;
                    transformedApptFormMetas.set(metadataValue, item);
                });

                // Validate meta to be reordered are present
                const hasRequesterLink = transformedApptFormMetas.has(MetadataEnum.REQUESTER_LINK);
                const hasRequesterName = transformedApptFormMetas.has(MetadataEnum.REQUESTER_NAME);
                const hasComments = transformedApptFormMetas.has(MetadataEnum.COMMENTS);
                const hasAccompanyingPerson = transformedApptFormMetas.has(MetadataEnum.ACCOMPANYING_PERSON);
                const hasBirthday = transformedApptFormMetas.has(MetadataEnum.BIRTHDAY);
                const hasScreeningFeesAcknowledged = transformedApptFormMetas.has(MetadataEnum.SCREENING_FEES_ACKNOWLEDGED);
                const hasCovidScreeningEmailConsent = transformedApptFormMetas.has(MetadataEnum.COVID_SCREENING_EMAIL_CONSENT);
                const hasCovidScreeningSecurityConsent = transformedApptFormMetas.has(MetadataEnum.COVID_SCREENING_SECURITY_CONSENT);

                const hasCovid511ConfirmReadDocument = transformedApptFormMetas.has(MetadataEnum.COVID_5_11_CONFIRM_READ_DOCUMENT);
                const hasCovid511ConfirmIAmParent = transformedApptFormMetas.has(MetadataEnum.COVID_5_11_CONFIRM_I_AM_PARENT);
                const hasCovid511ConfirmConsentToVaccine = transformedApptFormMetas.has(MetadataEnum.COVID_5_11_CONFIRM_CONSENT_TO_VACCINE);

                const hasPhxCovidScreeningDetailsConsent = transformedApptFormMetas.has(MetadataEnum.PHX_COVID_SCREENING_DETAILS_CONSENT);
                const hasPhxCovidScreeningConsent = transformedApptFormMetas.has(MetadataEnum.PHX_COVID_SCREENING_CONSENT);

                const hasCovidScreeningConsentReceiveResultByEmailSms = transformedApptFormMetas.has(MetadataEnum.COVID_SCREENING_CONSENT_RECEIVE_RESULT_BY_EMAIL_SMS);
                const hasCovidScreeningPreferredCommunication = transformedApptFormMetas.has(MetadataEnum.COVID_SCREENING_PREFERRED_COMMUNICATION);

                // Add requester link at beginning
                if (hasRequesterLink) {
                    remove(baseKeys, (value) => value === String(MetadataEnum.REQUESTER_LINK));
                    newOrderedArray.push(String(MetadataEnum.REQUESTER_LINK));
                }

                // Add requester name at beginning
                if (hasRequesterName) {
                    remove(baseKeys, (value) => value === String(MetadataEnum.REQUESTER_NAME));
                    newOrderedArray.push(String(MetadataEnum.REQUESTER_NAME));
                }

                // Add screening fees acknowledged at beginning
                if (hasScreeningFeesAcknowledged) {
                    remove(baseKeys, (value) => value === String(MetadataEnum.SCREENING_FEES_ACKNOWLEDGED));
                    newOrderedArray.push(String(MetadataEnum.SCREENING_FEES_ACKNOWLEDGED));
                }

                // Remove comments, will be added after everything below
                if (hasComments) {
                    remove(baseKeys, (value) => value === String(MetadataEnum.COMMENTS));
                }

                // Remove covid19 5 to 11 years old consents, will be added after everything below
                if (hasCovid511ConfirmReadDocument && hasCovid511ConfirmIAmParent && hasCovid511ConfirmConsentToVaccine) {
                    remove(baseKeys, (value) => value === String(MetadataEnum.COVID_5_11_CONFIRM_READ_DOCUMENT));
                    remove(baseKeys, (value) => value === String(MetadataEnum.COVID_5_11_CONFIRM_I_AM_PARENT));
                    remove(baseKeys, (value) => value === String(MetadataEnum.COVID_5_11_CONFIRM_CONSENT_TO_VACCINE));
                }

                // Remove covid19 screening consents, will be added after everything below
                if (hasCovidScreeningEmailConsent && hasCovidScreeningSecurityConsent) {
                    remove(baseKeys, (value) => value === String(MetadataEnum.COVID_SCREENING_EMAIL_CONSENT));
                    remove(baseKeys, (value) => value === String(MetadataEnum.COVID_SCREENING_SECURITY_CONSENT));
                }

                // Put Accompanying Person juste after Birthday if both meta are present
                if (hasAccompanyingPerson && hasBirthday) {
                    remove(baseKeys, (value) => value === String(MetadataEnum.ACCOMPANYING_PERSON));
                    const index = indexOf(baseKeys, String(MetadataEnum.BIRTHDAY));
                    baseKeys.splice(index + 1, 0, String(MetadataEnum.ACCOMPANYING_PERSON));
                }

                // Remove PHX_COVID_SCREENING_DETAILS_CONSENT and PHX_COVID_SCREENING_CONSENT, will be appeared inverted
                if (hasPhxCovidScreeningDetailsConsent && hasPhxCovidScreeningConsent) {
                    remove(baseKeys, (value) => value === String(MetadataEnum.PHX_COVID_SCREENING_DETAILS_CONSENT));
                    remove(baseKeys, (value) => value === String(MetadataEnum.PHX_COVID_SCREENING_CONSENT));
                }

                // Put COVID_SCREENING_PREFERRED_COMMUNICATION Before COVID_SCREENING_CONSENT_RECEIVE_RESULT_BY_EMAIL_SMS
                if (hasCovidScreeningConsentReceiveResultByEmailSms && hasCovidScreeningPreferredCommunication) {
                    remove(baseKeys, (value) => value === String(MetadataEnum.COVID_SCREENING_CONSENT_RECEIVE_RESULT_BY_EMAIL_SMS));
                    const index = indexOf(baseKeys, String(MetadataEnum.COVID_SCREENING_PREFERRED_COMMUNICATION));
                    baseKeys.splice(index + 1, 0, String(MetadataEnum.COVID_SCREENING_CONSENT_RECEIVE_RESULT_BY_EMAIL_SMS));
                }

                // Append all unordered after others
                newOrderedArray.push(...baseKeys);

                // Rebuild array of meta with proper base metadata objects
                let arrayMetaOrdered = [];
                for (let i = 0; i < newOrderedArray.length; i++) {
                    arrayMetaOrdered.push(transformedApptFormMetas.get(parseInt(newOrderedArray[i])));
                }

                // Append and change appearing order of PHX_COVID_SCREENING_DETAILS_CONSENT and PHX_COVID_SCREENING_CONSENT
                if (hasPhxCovidScreeningDetailsConsent && hasPhxCovidScreeningConsent) {
                    arrayMetaOrdered.push(transformedApptFormMetas.get(MetadataEnum.PHX_COVID_SCREENING_CONSENT));
                    arrayMetaOrdered.push(transformedApptFormMetas.get(MetadataEnum.PHX_COVID_SCREENING_DETAILS_CONSENT));
                }

                // Append comments at the end
                if (hasComments) {
                    arrayMetaOrdered.push(transformedApptFormMetas.get(MetadataEnum.COMMENTS));
                }

                // Append covid19 5 to 11 years old consents at the end after comments
                if (hasCovid511ConfirmReadDocument && hasCovid511ConfirmIAmParent && hasCovid511ConfirmConsentToVaccine) {
                    arrayMetaOrdered.push(transformedApptFormMetas.get(MetadataEnum.COVID_5_11_CONFIRM_READ_DOCUMENT));
                    arrayMetaOrdered.push(transformedApptFormMetas.get(MetadataEnum.COVID_5_11_CONFIRM_I_AM_PARENT));
                    arrayMetaOrdered.push(transformedApptFormMetas.get(MetadataEnum.COVID_5_11_CONFIRM_CONSENT_TO_VACCINE));
                }

                // Append covid19 screening consents at the end after comments
                if (hasCovidScreeningEmailConsent && hasCovidScreeningSecurityConsent) {
                    arrayMetaOrdered.push(transformedApptFormMetas.get(MetadataEnum.COVID_SCREENING_EMAIL_CONSENT));
                    arrayMetaOrdered.push(transformedApptFormMetas.get(MetadataEnum.COVID_SCREENING_SECURITY_CONSENT));
                }

                return arrayMetaOrdered;
            },
        },
        getServiceStep() {
            return find(this.steps, ['name', 'service']);
        },
        getPlaceStep() {
            return find(this.steps, ['name', 'place']);
        },
        getMomentStep() {
            return find(this.steps, ['name', 'moment']);
        },
        getServiceFiltersQuestionStep() {
            return find(this.steps, ['name', 'serviceFilters']);
        },
        getDataCollectionFormStep() {
            return find(this.steps, ['name', 'dataCollectionForm']);
        },
        places() {
            if (this.placesOrderByDistance) {
                return this.placesOrderByDistance;
            }
            return this.getPlaces;
        },

        /**
         * @description Returns whether the current view month contains availabilities
         * @returns {number}
         */
        monthHasAllowedAvailabilities() {
            return filter(this.getAvailabilities, (avail) => {
                const date = format(parse(avail.start), 'YYYY-MM-DD');
                const past = this.isDatePastEvent(date);
                const upcoming = this.isDateUpcomingEvent(date);
                const delayAndTolerance = (!this.serviceHasVaccineSubmodule && !this.serviceHasPregnancyDelaySubmodule) ||
            (this.isDateInsideTolerance(date) && !this.isDateCompleteEvent(date));

                return !past && !upcoming && delayAndTolerance;
            }).length;
        },

        /**
        * Time slots can be organized differently in memory based on settings.
        */
        hasTimeSlots() {
            if (!isArray(this.timeSlots) && isObject(this.timeSlots)) {
                return reduce(this.timeSlots, (sum, timeSlot) => sum + timeSlot.length, 0);
            }
            else if (isArray(this.timeSlots)) {
                return this.timeSlots.length > 0;
            }
            else {
                return false;
            }
        },

        /**
         * Enable submit button to book new appointment
         */
        disabledSubmit() {
            const blackListAndCaptchaNotEntered = !this.isIpWhitelisted && isNil(this.recaptchaToken);

            return !this.consentCollectData || this.submitting || this.appointmentCreated || !this.tosAndGDPRAgreement || this.containInvalidInputs || blackListAndCaptchaNotEntered;
        },

        /**
         * Validate if service template id is covid19
         */
        isCovid19Service() {
            const serviceTemplateId = this.selectedService.serviceTemplate.id;
            return serviceTemplateId === ServiceTemplateEnum.VACCINATION_COVID_19;
        },

        hasGenericVaccineService() {
            const serviceTemplateId = get(this.selectedService, 'serviceTemplate.id', -1);
            return includes(GenericVaccineServiceTemplateIds, serviceTemplateId);
        },

        /**
         * Validate if service template id is flu
         */
        isFluService() {
            const serviceTemplateId = this.selectedService.serviceTemplate.id;
            return serviceTemplateId === ServiceTemplateEnum.VACCINATION_GRIPPE;
        },

        showToleranceChipLegend() {
            return this.delayTolerance.minDelay !== this.delayTolerance.minTolerance ||
          this.delayTolerance.maxDelay !== this.delayTolerance.maxTolerance;
        },

        /**
         * Compute current step
         * @returns {*}
         */
        currentStep() {
            return this.steps[this.stepper - 1];
        },

        showStatusLegend() {
            return this.showCalendar && (this.serviceHasVaccineSubmodule || this.serviceHasPregnancyDelaySubmodule);
        },

        showCalendarComponent() {
            return this.selectedService && this.selectedPlace && !isEmpty(this.selectedPlace) && this.showCalendar;
        },

        shouldShowCtaBanner() {
            if (!isUsingKc) {
                return !this.getLoggedUser;
            }
            return !this.getLoggedUser || !this.getIsOwnerRegisterComplete;
        },

        accessibilityContinueButtonAriaLabel() {
            return this.$t('appt.accessibility.continueButtonLabel', [this.localizedDay(this.selectedDate), this.apptForm.time, this.selectedService['name' + upperFirst(this.locale)]]);
        },

        btnSubmitClass() {
            return this.disabledSubmit
                ? `btn-submit-disabled`
                : `button-secondary-color`;
        },
        showAccessibilityRequiredFields() {
            return !!this.formInputs.length;
        },
        containInvalidInputs() {
            return filter(this.formInputs, input => input.valid === false).length;
        }
    },
    methods: {
        ...mapActions({
            getMonthAvailabilities: 'availabilities/getAvailabilities',
            getCompleteAvailabilitiesBetweenPeriod: 'availabilities/getCompleteAvailabilitiesBetweenPeriod',
            getFirstAvailability: 'availabilities/getFirstAvailability',
            fetchClosestAvails: 'availabilities/fetchClosestAvails',
            createAppt: 'appointments/createAppt',
            getCommunications: 'establishment/getCommunications',
            toggleHomeLoading: 'loading/toggleHomeLoading',
            sendAppointmentMessage: 'appointments/sendAppointmentMessage',
            fetchEstMetadataValues: 'services/fetchEstMetadataValues',
            incrementPublicVisits: 'stats/incrementPublicVisits',
            insertKPIStatistic: 'stats/insertKPIStatistic',
            purgeAvailsPastDayUpcomingDaysAndDaysComplete: 'availabilities/purgeAvailsPastDayUpcomingDaysAndDaysComplete',
            setApiSecurityWhitelistingIp: 'setApiSecurityWhitelistingIp',
            getEstSettings: 'services/getEstSettings',
            openSignInWindow: 'jwt/openSignInWindow',
            openKcSignInWindow: 'kcLogin/openSignInWindow',
            openKcRegisterWindow: 'kcLogin/openRegisterWindow',
            getInstantiateServices: 'services/getInstantiateServices',
        }),

        ...mapMutations({
            setLocaleForProfile: 'jwt/SET_LOCALE_FOR_PROFILE',
            setServiceHasDataCollectionFormId: 'services/SET_SERVICE_HAS_DATA_COLLECTION_FORM_ID',
        }),

        hasEditableStepper(stepper, number) {
            if(stepper === number) { return true; }
            if (this.getServiceHasDataCollectionFormId &&
                get(this.currentStep, 'name') === get(this.getDataCollectionFormStep, 'name')) {
                return false;
            }
            return stepper >= number;
        },
        getTimeDescriptionAria(time) {
            if (this.apptForm.time === time) {
                return this.getTimeDescriptionAriaSelected(time);
            }
            let timeDescription = 'appt.accessibility.timeDescription';

            timeDescription += (this.apptForm.time === null) ? '.default' : '.alternate';

            return this.getDynamicTimeDescriptionAria(timeDescription, time);
        },

        getTimeDescriptionAriaSelected(time) {
            return this.getDynamicTimeDescriptionAria('appt.accessibility.timeDescriptionSelected.default', time);
        },

        getDynamicTimeDescriptionAria(timeDescription, time) {
            return this.$t(timeDescription, {
                time,
                date: this.localizedDay(this.selectedDate),
                serviceName: this.selectedService['name' + upperFirst(this.locale)],
            });
        },

        localizedDay(date) {
            let day = this.getDay(date);
            return (this.locale === 'fr') ? day + ' ' + this.calendarTitle.toLowerCase() : this.calendarTitle.replace(' ', ` ${day} `);
        },

        getDay(date) {
            return format(date, 'DD', { locale: this.locale === 'fr' ? frLocale : null });
        },

        /**
     * Get the milliseconds timestamp of the given date
     */
        timeSlotToMS(date, time) {
            let ts = new Date(`${date} ${time}`);
            return getTime(ts);
        },

        getStepHeaderTextKey(name) {
            return this.$t(headerTextMap[name]) || '';
        },
        stepAriaLabel(s) {
            const stepName = this.getStepHeaderTextKey(s.name);

            const stepProgresss = replace(replace(this.$t('appt.stepProgresss'), '@num', s.num), '@total', this.steps.length);
            if (this.stepper > s.num) {
                let stepValue = '';
                if (s.name === 'service') {
                    if (this.selectedService) {
                        stepValue = this.locale === 'en' ? this.selectedService.nameEn : this.selectedService.nameFr;
                    }
                }
                if (s.name === 'place') {
                    if (this.selectedPlace) {
                        stepValue = this.locale === 'en' ? this.selectedPlace.nameEn : this.selectedPlace.nameFr;
                    }
                }
                if (s.name === 'moment') {
                    stepValue = this.selectedDate;
                }
                if (s.name === 'data') {
                    stepValue = stepName;
                }
                if (s.name === 'dynamicServiceFilters') {
                    stepValue = 'Questions';
                }
                if(s.name === 'dataCollectionForm') {
                    stepValue = 'dataCollectionForm';
                }
                return `${this.$t('appt.completedStep')}: ${stepProgresss}, ${stepValue}.`;
            }
            else if (this.stepper === s.num) {
                let stepExtraDescription = s.name === 'dataCollectionForm' ? this.$t('appt.dataCollectionAriaDescription') : '';
                return `${this.$t('appt.currentStep')}: ${stepProgresss}, ${stepName}, ${stepExtraDescription}. `;
            }
            return undefined;
        },

        /**
     * Book another appointment
     */
        async bookAnotherAppointment() {
            this.selectedPlace = find(this.getPlaces, ['id', this.getNextAppointmentInfo.selectedPlace]);
            this.selectedService = find(this.getServices, ['id', this.getNextAppointmentInfo.selectedService]);
            await this.getEstSettings();

            this.checkForEstSettingsChange(this.currentEstSettings);

            if (size(this.filterQuestions)) {
                await this.toNextStep(this.getServiceStep.num);
            }
            else {
                await this.gotoCalendarStep();
                await this.selectDateAndClosestTimeSlot();
            }
        },

        /**
     * In book next appointment state, select the date, find the time or closest time and select it
     */
        async selectDateAndClosestTimeSlot() {
            if (!isNil(this.getNextAppointmentInfo)) {
                this.selectedDate = format(this.getNextAppointmentInfo.selectedDate, 'YYYY-MM-DD');
                await this.dayClick(this.selectedDate);

                if (this.timeSlots && this.timeSlots[0]) {
                    let selectedTimeMS = getTime(this.getNextAppointmentInfo.selectedDate);
                    this.apptForm.time = this.timeSlots[0].time;

                    /* find next closest timeslot if current timeslot is not available */
                    let minDiff = Math.abs(this.timeSlots[0].avail.start - selectedTimeMS);
                    forEach(this.timeSlots, (timeSlot, key) => {
                        let ms = this.timeSlotToMS(this.selectedDate, timeSlot.time);
                        let diff = Math.abs(ms - selectedTimeMS);
                        if (diff <= minDiff) {
                            minDiff = diff;
                            this.apptForm.time = timeSlot.time;
                            if (diff === 0) {
                                return false;
                            }
                        }
                    });
                    this.scrollTo('time-selector-anchor');
                }
            }
        },

        /**
     * Selects a date from the calendar to load availabilities
     */
        async dateClick(date) {
            if (date !== this.selectedDate) {
                this.selectedDate = date;
                await this.dayClick(date);
            }
        },

        /**
     * Moves to the previous calendar month
     */
        async previousMonth() {
            this.$refs.calendar[0].$refs.calendar.prev();
            await this.fetchAvailabilitiesBetweenDates();
            avoidNoAppointmentFocus();
            setFocusForAccessibility('previousMonth');
        },

        /**
     * Moves to the next calendar month
     */
        async nextMonth() {
            this.$refs.calendar[0].$refs.calendar.next();
            await this.fetchAvailabilitiesBetweenDates();
            avoidNoAppointmentFocus();
            setFocusForAccessibility('nextMonth');
        },

        /**
     * @description To fetch the value of EstablishmentSetting
     * @param setting
     * @return mixed
     */
        fetchEstablishmentSetting(setting) {
            return this.getEstablishmentSettingById(EstablishmentSetting[setting]);
        },

        /**
     * @description Set the new appointment as couponAppointment in store and routes to coupon page
     * @param appointments
     */
        async apptPrint(appointments = null) {
            await this.$router.push({ name: 'coupon' });
            this.toggleHomeLoading({ set: true });
        },

        /**
     * @description Calculated the Target period and the tolerance period for service for kid with delay and tolerance metadata
     */
        calculateKidsDelayTolerance() {
            if (this.selectedService && this.selectedService.serviceTemplate) {
                if (this.serviceHasVaccineSubmodule) {
                    const delayMinMonths = find(this.currentEstSettings, ['setting', ServiceSettings.VACCINE_DELAY_MIN_MONTHS]);
                    const delayMaxMonths = find(this.currentEstSettings, ['setting', ServiceSettings.VACCINE_DELAY_MAX_MONTHS]);

                    const perfectMinDate = format(addMonths(this.birthdayAnswer, delayMinMonths.value), 'YYYY-MM-DD');
                    const perfectMaxDate = format(addMonths(this.birthdayAnswer, delayMaxMonths.value), 'YYYY-MM-DD');
                    const delayMin = find(this.currentEstSettings, ['setting', ServiceSettings.VACCINE_DELAY_MIN]);
                    const delayMax = find(this.currentEstSettings, ['setting', ServiceSettings.VACCINE_DELAY_MAX]);
                    const toleranceBefore = find(this.currentEstSettings, ['setting', ServiceSettings.VACCINE_TOLERANCE_BEFORE]);
                    const toleranceAfter = find(this.currentEstSettings, ['setting', ServiceSettings.VACCINE_TOLERANCE_AFTER]);

                    if (this.birthdayAnswer && delayMin && delayMax && toleranceBefore && toleranceAfter) {
                        this.calculateDelayTolerance(perfectMinDate, delayMin, perfectMaxDate, delayMax, toleranceBefore, toleranceAfter);
                    }
                }
            }
        },

        calculateDelayTolerance(perfectMinDate, delayMin, perfectMaxDate, delayMax, toleranceBefore, toleranceAfter) {
            this.delayTolerance.minDelay = format(subDays(perfectMinDate, delayMin.value), 'YYYY-MM-DD');
            this.delayTolerance.maxDelay = format(addDays(perfectMaxDate, delayMax.value), 'YYYY-MM-DD');
            this.delayTolerance.minTolerance = format(subDays(this.delayTolerance.minDelay, toleranceBefore.value), 'YYYY-MM-DD');
            this.delayTolerance.maxTolerance = format(addDays(this.delayTolerance.maxDelay, toleranceAfter.value), 'YYYY-MM-DD');
        },

        /**
     * @description Calculated the Target period and the tolerance period for service for pregnancy with delay and tolerance metadata
     */
        calculatePregnancyDelayTolerance() {
            if (this.selectedService && this.selectedService.serviceTemplate) {
                if (this.serviceHasPregnancyDelaySubmodule) {
                    const delayMinDays = find(this.currentEstSettings, ['setting', ServiceSettings.PREGNANCY_DELAY_MIN_DAYS]);
                    const delayMaxDays = find(this.currentEstSettings, ['setting', ServiceSettings.PREGNANCY_DELAY_MAX_DAYS]);
                    const firstDayOfPregnancy = format(subMonths(this.pregnancyDpa, 9), 'YYYY-MM-DD');

                    const perfectMinDate = format(addDays(firstDayOfPregnancy, delayMinDays.value), 'YYYY-MM-DD');
                    const perfectMaxDate = format(addDays(firstDayOfPregnancy, delayMaxDays.value), 'YYYY-MM-DD');
                    const delayMin = find(this.currentEstSettings, ['setting', ServiceSettings.PREGNANCY_DELAY_MIN]);
                    const delayMax = find(this.currentEstSettings, ['setting', ServiceSettings.PREGNANCY_DELAY_MAX]);
                    const toleranceBefore = find(this.currentEstSettings, ['setting', ServiceSettings.PREGNANCY_TOLERANCE_BEFORE]);
                    const toleranceAfter = find(this.currentEstSettings, ['setting', ServiceSettings.PREGNANCY_TOLERANCE_AFTER]);

                    if (this.pregnancyDpa && delayMin && delayMax && toleranceBefore && toleranceAfter) {
                        this.calculateDelayTolerance(perfectMinDate, delayMin, perfectMaxDate, delayMax, toleranceBefore, toleranceAfter);
                    }
                }
            }
        },

        checkForEstSettingsChange(settings) {
            this.filterQuestions = {};
            this.serviceFiltersAnswers = [];
            /* check if the service have at least one meta filter question because you should not display an empty filters section */
            const enableFilterQuestions = find(settings, ['setting', ServiceSettings.ENABLE_FILTER_QUESTIONS]);
            if (!!enableFilterQuestions && enableFilterQuestions.value) {
                const questionsMetas = find(settings, ['setting', ServiceSettings.FILTER_QUESTIONS]);
                this.filterQuestions = questionsMetas
                    ? questionsMetas.value
                    : [];
            }

            if (size(this.filterQuestions)) {
                if (!includes(this.arrSteps, 'serviceFilters')) {
                    this.arrSteps.splice(this.arrSteps.indexOf('service') + 1, 0, 'serviceFilters');
                    this.mapSteps();
                }
            }
            else {
                if (includes(this.arrSteps, 'serviceFilters')) {
                    this.arrSteps.splice(this.arrSteps.indexOf('service') + 1, 1);
                    this.mapSteps();
                }
            }
        },

        /**
     * @description Validate email and checkbox of optinNewServiceList checked
     * @returns {boolean}
     */
        confirmCheckOptinNewServiceList() {
            return !(this.optinNewServiceList && (isNil(this.apptForm.clientData.email) || !this.apptForm.clientData.email));
        },

        /**
     * @description When a date is clicked in the date picker
     * @param date
     */
        async dayClick(date) {
            this.selectedDate = date;
            await this.fetchAvailabilitiesForDay();
            this.apptForm.time = null;
            this.scrollTo('time-selector-anchor');
            this.$nextTick(() => {
                if (get(this.$refs, 'timeSelectorAnchor[0]')) {
                    this.$refs.timeSelectorAnchor[0].focus({ preventScroll: true });
                }
            });
            await this.publicSelectAvailabilityDate(date);
        },

        /**
     * @description Fetch availabilities for one selected date
     */
        async fetchAvailabilitiesForDay() {
            try {
                this.isFetchingDayAvails = true;
                this.timeSlots = [];

                const dateStart = this.selectedDate;
                const dateStop = format(addDays(this.selectedDate, 1), 'YYYY-MM-DD');
                const placeId = this.selectedPlace.id !== 0 && !this.orderByEstablishment ? this.selectedPlace.id : null;
                const filters = this.mapServiceFiltersAnswers();

                await this.getCompleteAvailabilitiesBetweenPeriod([
                    dateStart,
                    dateStop,
                    this.selectedService.id,
                    placeId,
                    filters,
                    this.clientTimeZone,
                ]);

                await this.showAvailabilities();
            }
            finally {
                this.isFetchingDayAvails = false;
            }
        },

        /**
     * @description Fetch availabilities between 2 dates with light info
     * @returns {Q.Promise<any> | * | Q.Promise<T | never> | PromiseLike<T | never> | Promise<T | never>}
     */
        async fetchAvailabilitiesBetweenDates() {
            try {
                this.isFetchingDatesAvails = true;
                const dateStart = format(this.start, 'YYYY-MM') + '-01';
                const dateStop = format(addDays(endOfMonth(this.start), 1), 'YYYY-MM-DD');
                const placeId = this.selectedPlace.id !== 0 && !this.orderByEstablishment ? this.selectedPlace.id : null;
                const filters = this.mapServiceFiltersAnswers();
                await Promise.all([
                    this.getMonthAvailabilities([dateStart, dateStop, this.selectedService.id, placeId, filters, this.clientTimeZone]),
                    this.showAvailabilities(),
                ]);

                if (this.serviceHasVaccineSubmodule || this.serviceHasPregnancyDelaySubmodule) {
                    await this.purgeAvailsPastDayUpcomingDaysAndDaysComplete(
                        {
                            dateMin: this.delayTolerance.minTolerance,
                            dateMax: this.delayTolerance.maxTolerance,
                        });
                }
            }
            finally {
                this.isFetchingDatesAvails = false;
            }
        },

        /**
     * @description Returns the appointment object for creation
     * @returns {Object}
     */
        getAppointmentDataFromForm() {
            const timeSlot = ((this.orderByEstablishment || this.orderByResource) && !this.showAvailabilitiesOfAllPlaces)
                ? find(this.timeSlots[this.apptForm.selectedGroupTime], { 'time': this.apptForm.time })
                : find(this.timeSlots, { 'time': this.apptForm.time });

            if (!timeSlot) {
                throw new TrimozError(new Error(), 'errors.generic');
            }

            const formatDate = 'YYYY-MM-DD HH:mm:ss.SSS';
            let start = format(parse(this.selectedDate + ' ' + this.apptForm.time.substr(0, 5)), formatDate);
            let stop = format(addMinutes(start, this.selectedService.length), formatDate);

            start = parseFromTimeZone(start, { timeZone: this.clientTimeZone });
            start = formatToTimeZone(start, formatDate, { timeZone: 'UTC' });
            stop = parseFromTimeZone(stop, { timeZone: this.clientTimeZone });
            stop = formatToTimeZone(stop, formatDate, { timeZone: 'UTC' });

            let clientWallclock = this.apptForm.time.substr(0, 5);

            let resourceIdWithHighestCount = this.selectedService.activateMultiAppointment
                ? maxBy(keys(timeSlot.resourceLink), (o) => {
                    return timeSlot.resourceLink[o];
                })
                : timeSlot.avail.resource;

            if (this.apptForm.clientData.birthday) {
                this.apptForm.clientData.birthday = replace(this.apptForm.clientData.birthday, /(\d{4})(\d{2})(\d{2})/g, '$1-$2-$3');
            }

            if (this.apptForm.clientData.birthday === '') {
                const { birthday, ...apptFormWithoutBirthday } = this.apptForm.clientData;
                this.apptForm.clientData = apptFormWithoutBirthday;
            }

            const resourcesCount = {};
            if (this.selectedService.activateMultiAppointment) {
                resourcesCount[resourceIdWithHighestCount] = 0;
                for (let i = 0; i < this.apptForm.nbr; i++) {
                    if (timeSlot.resourceLink[resourceIdWithHighestCount] > 0) {
                        timeSlot.resourceLink[resourceIdWithHighestCount]--;
                    }
                    else {
                        resourceIdWithHighestCount = maxBy(keys(timeSlot.resourceLink), (o) => {
                            return timeSlot.resourceLink[o];
                        });
                        resourcesCount[resourceIdWithHighestCount] = 0;
                        timeSlot.resourceLink[resourceIdWithHighestCount]--;
                    }
                    resourcesCount[resourceIdWithHighestCount]++;
                }
            }
            else {
                resourcesCount[resourceIdWithHighestCount] = 1;
            }

            let scannedRequestFile = {};
            if (this.apptForm.clientData.scanned_request) {
                scannedRequestFile = this.apptForm.clientData.scanned_request;
                this.apptForm.clientData.scanned_request_name = scannedRequestFile.name;
            }

            this.appointmentDataForm = assign({},
                { id: '' },
                { place: timeSlot.avail.place },
                { source: 'public' },
                { locale: this.$i18n.locale + '-ca' },
                { clientState: 1 },
                { resourcesCount: JSON.stringify(resourcesCount) },
                { dateStart: dateTimeToString(start) },
                { dateStop: dateTimeToString(stop) },
                { timezone: this.clientTimeZone },
                { clientWallclock: clientWallclock },
                { establishmentServiceTemplate: this.selectedService.id },
                { optinNewServiceList: this.optinNewServiceList },
                { piConsent: 1 },
                { delegatedPiConsent: this.delegatedPiConsent },
                { clientData: JSON.stringify(this.apptForm.clientData) },
                { scannedRequest: size(scannedRequestFile) > 0 ? scannedRequestFile.file : null },
                { recaptchaToken: this.isIpWhitelisted ? '' : this.recaptchaToken }
            );

            if (this.patientUuid && this.patientUuid !== -1) {
                this.appointmentDataForm.patientUuid = this.patientUuid;
            }

            // Parameters to send messages through the api instead
            this.appointmentDataForm.configurationMessages = {};
            const configurationMessageNewAppt = ConfigurationMessageEnum.NEW_APPOINTMENT;

            if (this.shouldSendEmail()) {
                const messageTypeMail = MessageTypeEnum.MAIL;
                this.appointmentDataForm.configurationMessages[messageTypeMail] = {};
                this.appointmentDataForm.configurationMessages[messageTypeMail][configurationMessageNewAppt] = 1;
            }

            if (this.shouldSendSms()) {
                const messageTypeMail = MessageTypeEnum.SMS;
                this.appointmentDataForm.configurationMessages[messageTypeMail] = {};
                this.appointmentDataForm.configurationMessages[messageTypeMail][configurationMessageNewAppt] = 1;
            }

            if (this.appointmentDataForm.configurationMessages) {
                this.appointmentDataForm.configurationMessages = JSON.stringify(
                    this.appointmentDataForm.configurationMessages
                );
            }
        },

        /**
     * @description Filters the availabilities with a length smaller than it's service length
     * @param {Array} avails
     * @returns {Array}
     */
        filterFakeAvailabilities(avails) {
            return compact(map(avails, (avail) => {
                if (avail.date !== avail.dateEnd) {
                    if (avail.date === this.selectedDate) {
                        avail.dateEnd = avail.date;
                        avail.stop = formatToTimeZone(parseFromTimeZone(parse(avail.dateEnd + ' 24:00:00'), { timeZone: this.clientTimeZone }), 'YYYY-MM-DD HH:mm:ss', { timeZone: this.clientTimeZone });
                        avail.stop = parse(avail.stop).getTime();
                    }
                    else if (avail.dateEnd === this.selectedDate) {
                        avail.date = avail.dateEnd;
                        avail.start = formatToTimeZone(parseFromTimeZone(parse(avail.date + ' 00:00:00'), { timeZone: this.clientTimeZone }), 'YYYY-MM-DD HH:mm:ss', { timeZone: this.clientTimeZone });
                        avail.start = parse(avail.start).getTime();
                    }

                    if (avail.stop - avail.start < avail.length * 60 * 1000) {
                        return null;
                    }
                }
                return avail;
            }));
        },

        /**
         * @description Submits the appointment for creation
         */
        async formSubmit() {
            this.isFormValid = get(this.$refs, 'appointmentForm[0]') ? this.$refs.appointmentForm[0].validate() : false;

            if (this.isFormValid && this.disabledSubmit === false) {
                this.apptForm.clientData = normalizeFormData(this.apptForm.clientData);
                const clientData = humps(this.apptForm.clientData);

                validateNamAgainstBirthday(clientData);
                validateMinAge(clientData, this.currentEstSettings);

                try {
                    // Try to book the appointment
                    this.submitting = true;
                    this.createdAppointmentData = await this.createAppointment();
                    await this.$store.commit('services/SET_ASSOCIATED_EST', []);
                    await this.$store.commit('services/RESET_SERVICES_FILTERED_BY_SERVICE_TEMPLATE_IDS');

                    // GA4 GTM event
                    this.publicTakeApptAnalytics(this.createdAppointmentData);

                    // Show the coupon or show data collection form
                    if (this.getServiceHasDataCollectionFormId && this.currentStep.name === 'data') {
                        await this.toNextStep(this.getDataCollectionFormStep.num - 1);
                    }
                    else {
                        await this.setupCoupon(this.createdAppointmentData);
                        this.recaptchaToken = null;
                    }
                }
                catch (error) {
                    const internalCode = get(error, 'internalCode');

                    if (internalCode === 'appointment.unavailable_availability') {
                        await this.getClosestAvail();
                        this.setRegularServiceTimeSlots();
                    }
                    else {
                        if (startsWith(internalCode, 'validation.birthday.min_age_to_book')) {
                            throw new TrimozError(error, 'errors.validation.birthday.min_age_to_book');
                        }

                        if (internalCode === 'validation.birthday.max_age_to_book_patient') {
                            throw new TrimozError(error, 'errors.validation.birthday.max_age_to_book');
                        }

                        if (startsWith(internalCode, 'patient.appointment.cannot-link-patient-using-current-user')) {
                            throw new TrimozError(error, 'errors.patient.appointment.cannot-link-patient-using-current-user');
                        }

                        if (internalCode === 'profile.invalid-nam') {
                            throw new TrimozError(error, 'errors.invalid.nam');
                        }

                        throw error;
                    }
                }
                finally {
                    this.submitting = false;
                    this.$store.commit('metadataForm/SET_PATIENT_DATA_TO_FILL_METADATA_FORM', undefined);
                }
            }
            else {
                this.$store.commit('snackbar/SET_SNACK', {
                    message: this.$i18n.messages[this.locale].base.errors.requiredFields,
                    color: 'error',
                    timeout: -1,
                });
                if (get(this.$refs, 'appointmentForm[0]')) {
                    this.formInputs = this.$refs.appointmentForm[0].inputs;
                }
                setFocusForAccessibility('accessibilityRequiredFields');
            }
        },

        async showCoupon() {
            await this.setupCoupon(this.createdAppointmentData);
            this.recaptchaToken = null;
        },

        updateAccessibilityRequiredFields() {
            if (get(this.$refs, 'appointmentForm[0]')) {
                this.formInputs = this.$refs.appointmentForm[0].inputs;
            }
            else {
                this.formInputs = [];
            }
        },

        /**
     * Get closest avails when the previous selected timeslot is no longer available
     */
        async getClosestAvail() {
            const resourceCount = JSON.parse(this.appointmentDataForm.resourcesCount);
            const rc = reduce(resourceCount, (result, val) => {
                result += val;
                return result;
            }, 0);
            const dateStart = format(this.appointmentDataForm.dateStart, 'YYYY-MM-DD');
            const dateStop = format(addDays(this.appointmentDataForm.dateStart, 1), 'YYYY-MM-DD');
            const filters = this.mapServiceFiltersAnswers();
            try {
                await this.fetchClosestAvails([
                    this.appointmentDataForm.dateStart,
                    dateStart,
                    dateStop,
                    this.appointmentDataForm.establishmentServiceTemplate,
                    this.appointmentDataForm.place,
                    filters,
                    this.appointmentDataForm.timezone,
                    5,
                    rc,
                ]);
                this.closestAvailsModal = true;
            }
            catch (error) {
                if (get(error, 'internalCode') === 'availabilities.public.nothing-for-day') {
                    this.closestAvailsModal = true;
                }
                else {
                    throw new TrimozError(error, 'errors.generic');
                }
            }
        },

        /**
     * Creates the appointment from entered/selected data
     */
        async createAppointment() {
            this.getAppointmentDataFromForm();

            const response = await this.createAppt(this.appointmentDataForm);

            const stepName = this.steps[this.stepper];

            if (stepName !== undefined) {
                if (stepName.name === 'dataCollectionForm') {
                    // displays a special message to warn user of data collection form
                    this.$store.commit('snackbar/SET_SNACK', {
                        message: this.$i18n.messages[this.locale].appt.notifications.addSuccess,
                        lowerMessage: this.$i18n.messages[this.locale].appt.notifications.dataCollectionForm,
                        color: 'success',
                        timeout: -1,
                    });
                }
                else{
                    this.$store.commit('snackbar/SET_SNACK', {
                        message: this.$i18n.messages[this.locale].appt.notifications.addSuccess,
                        color: 'success',
                        timeout: -1,
                    });
                }
            }
            else{
                this.$store.commit('snackbar/SET_SNACK', {
                    message: this.$i18n.messages[this.locale].appt.notifications.addSuccess,
                    color: 'success',
                    timeout: -1,
                });
            }

            this.appointmentCreated = true;
            return response;
        },

        /**
     * Creates the appointment from entered/selected data with the new Timeslot
     */
        async createAppointmentWithNewTimeslot(newTime) {
            this.apptForm.time = newTime;
            this.closestAvailsModal = false;
            await this.formSubmit();
        },

        /**
     * Returns whether mail confirmation should be sent
     */
        shouldSendEmail() {
            return this.apptForm.sendByEmail && this.hasMetaIn('email');
        },

        /**
     * Returns whether sms confirmation should be sent
     */
        shouldSendSms() {
            return this.apptForm.sendBySMS && this.hasMetaIn('cellphone') && this.sendConfirmationSms;
        },

        /**
     * Displays the coupon once the appointment has been booked
     */
        async setupCoupon(apptResponse) {
            // Attempt to get the communications and print the coupon to screen
            let attempts = 0;
            let setupComplete = false;
            while (setupComplete === false && attempts < 3) {
                try {
                    // Get the communications settings
                    await this.getCommunications();

                    // Fetch the appointment generated and setup the coupon to print back
                    await this.apptPrint(apptResponse);
                    this.resetPage();
                    setupComplete = true;
                }
                catch (error) {
                    attempts++;
                }
            }
            if (!setupComplete) {
                this.errorModal = true;
                this.errorKey = 'appointmentBookedButErrorDisplayingCoupon';
                this.errorReplacements = {
                    appointment_number: apptResponse[0].appointmentNumber,
                };
            }
        },

        /**
     * @description Return string placeholder for metadata depending on current locale
     * @param {string} meta
     * @returns {string}
     */
        getPlaceholderFromMeta(meta) {
            return this.$i18n.messages[this.locale].appt.placeholder[camelCase(meta)];
        },

        /**
     * @description Get the place name by id
     * @param placeId
     * @returns {string}
     */
        getPlaceName(placeId) {
            let currentPlace = find(this.places, { id: parseInt(placeId) });
            return currentPlace['name' + this.upperLocale];
        },

        /**
     * @description Returns the places with 'all places' item
     * @returns {*}
     */
        getPlacesWithAllItem() {
            const places = cloneDeep(this.places);
            if (this.getEstablishmentSettingById(EstablishmentSetting.ACTIVATE_SEARCH_IN_ALL_SCHEDULES)) {
                const all = {
                    id: 0,
                    nameFr: 'Rechercher dans tous les lieux',
                    nameEn: 'Search in all places',
                };

                places.push(all);
            }
            return places;
        },

        /**
     * @description Appends the place name to the txt string
     * @param {number|string} resourceId
     * @returns {string}
     */
        getResourceFormatName(resourceId) {
            if (this.getResource(resourceId)) {
                return this.getResource(resourceId).formatName;
            }
            return '';
        },

        /**
     * @description Returns a boolean whether the appt form has metas
     * @returns {boolean}
     */
        hasMeta() {
            return some(this.apptForm.metas, { status: 1 });
        },

        /**
     * @description Returns a boolean whether the appt form has a meta
     * @param metaName
     * @returns {boolean}
     */
        hasMetaIn(metaName) {
            return this.apptForm.clientData[metaName];
        },

        /**
     * @description Returns whether the date is inside the vaccine delay tolerance
     * @param {Date} date
     * @returns {boolean}
     */
        isDateInsideTolerance(date) {
            return !isBefore(date, this.delayTolerance.minTolerance) &&
          !isAfter(date, this.delayTolerance.maxTolerance);
        },

        /**
     * @description Maps the arrSteps into steps
     */
        mapSteps() {
            let i = 0;
            this.steps = map(this.arrSteps, name => {
                return {
                    num: ++i,
                    name: name,
                    props: {},
                };
            });
        },

        /**
     * @description Maps and filters the service answers for backend
     * @returns {Array}
     */
        mapServiceFiltersAnswers() {
            return map(this.serviceFiltersAnswers, (filterAnswer) => {
                switch (parseInt(filterAnswer)) {
                    case 1:
                        return 0;
                    case 2:
                        return 1;
                    default:
                        return null;
                }
            });
        },

        /**
     * @description Sets the current place on panel input
     * @param placeIndex
     */
        async onPlacePanelInput(placeIndex) {
            if (!isNil(placeIndex) && placeIndex !== -1) {
                this.selectedPlace = this.getPlacesWithAllItem()[placeIndex];
                this.orderByEstablishment = this.selectedPlace.id === 0;
                this.timeSlots = [];
            }
        },

        /**
     * @description When the service filter component emits new answers
     * @param answers
     */
        onServiceFiltersAnswers(answers) {
            this.serviceFiltersAnswers = answers;
        },

        /**
     * @description When the stepper step was changed by input
     * @param step
     */
        async onStepperInput(step) {
            await this.checkForDataCollectionFormStep();
            const stepName = this.steps[step - 1].name;
            if (stepName === 'service' || stepName === 'place') {
                this.resetDatePicker();
            }
            else if (stepName === 'data' && this.$vuetify.breakpoint.mdAndUp) {
                this.scrollTo('stepper-header');
            }

            if (stepName === 'place' && this.$route.query.portalPlace) {
                const placeExist = find(this.getPlaces, ['id', parseInt(this.$route.query.portalPlace)]);
                if (placeExist) {
                    this.$store.commit('places/SET_SELECTED_PLACE_ID', this.$route.query.portalPlace);
                    await this.passDirectlyToMomentStep();
                }
            }

            /**
             * Auto select place and service and have service dynamic filters and portalEst found in available services
            */
            if (stepName === 'service' &&
                this.getPortalEst &&
                this.serviceHasDynamicFilter &&
                find(this.getServices, ['id', this.getPortalEst])
            ) {
                await this.prepareToGoToMomentStep();
            }
        },

        /**
     * @description Auto select service when arrive from portal
     * and url parameter portalEst is set
     */
        async prepareToGoToMomentStep() {
            await this.$store.dispatch('services/setSelectedPortalEST');
            await this.getEstSettings([this.selectedService.id]);
            this.checkForEstSettingsChange(this.currentEstSettings);

            await this.checkForDataCollectionFormStep();

            if (!size(this.filterQuestions)) {
                if (this.getPortalEst && !this.getSelectedPlaceId) {
                    await this.toNextStep(this.getPlaceStep.num - 1);
                }
                else {
                    await this.passDirectlyToMomentStep();
                }
            }
            else {
                await this.toNextStep((this.getServiceFiltersQuestionStep.num - 1));
            }
        },

        /**
     * @description Displays the establishment popup message
     * show the popup on the first visit, when chenge longuage
     * or 5 minutes after the last popup
     */
        popUpText() {
            if (this.getTexts[this.locale].isPopupActivated) {
                this.dialog = true;
                this.popUp.text = this.getTexts[this.locale].popupText;
                this.popUp.title = this.getTexts[this.locale].popupTitle;
            }
        },

        /**
     * @description Reduces an array of time slots by time
     * @param {Array} arrayTimeSlots
     * @returns {Object}
     */
        reduceArrayOfTimeSlotsByTime(arrayTimeSlots) {
            return reduce(arrayTimeSlots, (result, timeSlots, key) => {
                result[key] = uniqBy(timeSlots, 'time');
                return result;
            }, {});
        },

        /**
     * @description Reduces the time slot object by time, merging the resource counts and keeping track of the resources with their respective resource count
     * @param timeSlots
     * @returns {Array}
     */
        reduceTimeSlotsForMultiAppt(timeSlots) {
            return values(reduce(timeSlots, (result, timeSlotWithAvail) => {
                const currentTimeSlot = result[timeSlotWithAvail.time] ||
            (result[timeSlotWithAvail.time] = {
                resourceLink: {},
                time: timeSlotWithAvail.time,
                avail: cloneDeep(timeSlotWithAvail.avail),
            });

                if (currentTimeSlot.avail.id !== timeSlotWithAvail.avail.id) {
                    currentTimeSlot.avail.resourceCount += timeSlotWithAvail.avail.resourceCount;
                }

                currentTimeSlot.resourceLink[timeSlotWithAvail.avail.resource] = timeSlotWithAvail.avail.resourceCount;
                currentTimeSlot.timeLabel = currentTimeSlot.time + (currentTimeSlot.avail.resourceCount > 1 ? ' (' + currentTimeSlot.avail.resourceCount + ')' : '');
                return result;
            }, {}));
        },

        /**
     * @description Resets the page
     */
        resetPage() {
            if (this.$refs.appointmentForm[0]) {
                this.$refs.appointmentForm[0].reset();
            }
            this.apptForm.time = '';
            this.apptForm.nbr = 1;
            this.apptForm.max = 1;
            this.appointmentCreated = false;
            this.createdAppointmentData = {};
            this.timeSlots = {};
            this.$emit('apptTaken');
        },

        /**
     * @description Resets the date picker
     */
        resetDatePicker() {
            this.timeSlots = [];
            this.selectedDate = null;
            this.apptForm.time = null;
        },

        /**
     * @description Scroll to DOM element
     */
        scrollTo(element) {
            const scrollTo = scroller();
            scrollTo('#' + element);
        },

        /**
     * @description When a time is selected
     * @param time
     * @param group
     * @param resourceCount
     */
        async setApptGroupTime(time, group, resourceCount) {
            this.unSelectOtherRadioTime(group);
            this.apptForm.nbr = 1;
            this.apptForm.time = time;
            this.apptForm.selectedGroupTime = group;

            /* multi-rdv */
            if (this.selectedService.activateMultiAppointment) {
                this.apptForm.max = resourceCount > 10 ? 10 : resourceCount;
            }
            setFocusForAccessibility(`selected-time-slot-${time}`, 0, false);
        },

        /**
     * @description Sets the time slots for a regular service
     */
        setRegularServiceTimeSlots() {
            try {
                this.isFetchingTimeAvails = true;
                const isMultiAppt = this.selectedService.activateMultiAppointment;

                const avails = this.filterFakeAvailabilities(this.getDailyAvailabilities);
                avails.sort(function(a, b) {
                    return a.start - b.start;
                });

                this.timeSlots = this.extractTimeSlots(avails);

                if (this.orderByEstablishment) {
                    if (this.orderByResource) {
                        this.timeSlots = groupBy(this.timeSlots, s => {
                            return s.avail.place + ' - ' + s.avail.resource;
                        });
                    }
                    else {
                        this.timeSlots = groupBy(this.timeSlots, 'avail.place');

                        if (!isMultiAppt) {
                            this.timeSlots = this.reduceArrayOfTimeSlotsByTime(this.timeSlots);
                        }
                        else {
                            forEach(this.timeSlots, (placeTimeSlots, index) => {
                                this.timeSlots[index] = this.reduceTimeSlotsForMultiAppt(this.timeSlots[index]);
                            });
                        }
                    }
                }
                else if (this.orderByResource) {
                    this.timeSlots = groupBy(this.timeSlots, 'avail.resource');
                }
                else {
                    this.timeSlots = isMultiAppt
                        ? this.reduceTimeSlotsForMultiAppt(this.timeSlots)
                        : uniqBy(this.timeSlots, 'time');
                }

                if (this.getEstablishmentSettingById(EstablishmentSetting.ACTIVATE_SEARCH_IN_ALL_SCHEDULES)) {
                    let availByPlace = flatMap(this.timeSlots, slot => {
                        return map(slot, s => {
                            return {
                                avail: s.avail,
                                resourceLink: s.resourceLink,
                                time: s.time,
                                timeLabel: s.timeLabel,
                            };
                        });
                    });
                    this.timeSlots = sortBy(uniqBy(availByPlace, 'time'), 'time');
                    this.showAvailabilitiesOfAllPlaces = true;
                }
            }
            finally {
                this.isFetchingTimeAvails = false;
            }
        },

        /**
     * @description Displays the availabilities of the selected service
     */
        async showAvailabilities() {
            try {
                this.setRegularServiceTimeSlots();
                await this.fetchEstMetadataValues(this.selectedService.id);
                this.apptForm.metas = this.getOrderredCopySelectedServiceMetadataValuesOverrideApplied;

                /* set apptForm.meta value receive from clientData */
                forEach(this.apptForm.clientData, (value, key) => {
                    if (value) {
                        const metaId = MetadataEnum[toUpper(key)];
                        if (find(this.apptForm.metas, ['metadata', metaId])) {
                            const index = findIndex(this.apptForm.metas, ['metadata', metaId]);
                            this.apptForm.metas[index].value = value;
                        }
                    }
                });
            }
            catch (error) {
                this.showAvailabilitiesOfAllPlaces = false;
            }
        },

        /**
     * @description Get the name for a group of time slots
     */
        showGroupName(txt, groupId) {
            let arrId = split(groupId, '-');
            let place = '';
            let resource = '';
            const activateSearchInAllSchedules = this.getEstablishmentSettingById(EstablishmentSetting.ACTIVATE_SEARCH_IN_ALL_SCHEDULES);

            if (this.orderByEstablishment && !activateSearchInAllSchedules) {
                place = this.getPlaceName(arrId[0]);
                arrId = drop(arrId);
            }
            if (this.orderByResource) {
                resource = this.getResourceFormatName(arrId[0]);
                arrId = drop(arrId);
            }
            const formated = this.orderByEstablishment && this.orderByResource
                ? place + ' - ' + resource
                : place || resource;
            return txt + (!activateSearchInAllSchedules ? ' ( ' + formated + ' )' : '');
        },

        async gotoCalendarStep() {
            await this.toNextStep(this.getMomentStep.num - 1);
            this.closestAvailsModal = false;
        },

        async toPreviousStep(stepNum) {
            if(stepNum < 0) {
                return;
            }
            await this.toNextStep(stepNum - 2);
        },

        async toPreviousStepCalendar(stepNum) {
            const numberOfPreviousStep = this.getPlaces.length === 1 || this.getSelectedPlaceId ? 1 : 0;

            await this.toPreviousStep(stepNum - numberOfPreviousStep);
        },

        /**
     * @description Handles the navigation to next steps for the stepper component
     */
        async toNextStep(stepNum) {
            await this.checkForDataCollectionFormStep();
            if (this.steps[stepNum].name === 'moment') {
                this.initiallyNoAvails = true;
                this.resetDatePicker();
                this.calculateKidsDelayTolerance();
                this.calculatePregnancyDelayTolerance();
                await this.toMomentStep();
                this.$store.commit('places/SET_IS_SELECTED_PLACE', true);

                if (!isNil(this.getNextAppointmentInfo) && !isNil(this.getNextAppointmentInfo.secondAppointment)) {
                    await this.selectDateAndClosestTimeSlot();
                }
            }

            // If next step is place and that selected place is defined and it exists,or there is one place only, skip place step
            if (this.steps[stepNum].name === 'place') {
                const place = this.getPlaces.length === 1
                    ? this.getPlaces[0]
                    : find(this.getPlaces, ['id', parseInt(this.getSelectedPlaceId)]);

                if (place) {
                    this.selectedPlace = place;
                    await this.toNextStep(stepNum + 1);
                    return;
                }
                else {
                    await this.$store.dispatch('loading/toggleLoading', { set: false });
                }
            }

            // If next step is nominative data, reset recaptcha
            if (this.steps[stepNum].name === 'data') {
                this.recaptchaToken = null;
                // GTM event
                this.publicAvailabilitySelectedContinueButton(this.selectedDate, this.apptForm.time);
            }

            if (this.serviceHasDynamicFilter && this.steps[stepNum].name === 'service') {
                this.dynamicMetasResponses = this.getDynamicFilterMetadataValues;
            }

            this.stepper = stepNum + 1;
            this.scrollTo('app');
            if (get(this.$refs, 'topOfStepperRef')) {
                this.$refs.topOfStepperRef.focus({ preventScroll: true });
            }
        },

        /**
         * @description validate if service has data collection form id
         *              Add or remove step to stepper
         */
        async checkForDataCollectionFormStep() {
            if (this.getEstablishmentSettingById(EstablishmentSetting.ENABLE_DATA_COLLECTION_FORMS)) {
                await this.setServiceHasDataCollectionFormId();

                if (this.getServiceHasDataCollectionFormId && !includes(this.arrSteps, 'dataCollectionForm')) {
                    this.arrSteps.push('dataCollectionForm');
                    this.mapSteps();
                }
                else if (!this.getServiceHasDataCollectionFormId && includes(this.arrSteps, 'dataCollectionForm')) {
                    this.arrSteps.splice(this.arrSteps.indexOf('dataCollectionForm'), 1);
                    this.mapSteps();
                }
            }
        },

        /**
     * @description To 'choose a moment' step
     *              Fetches the first availability for the selected service/place(s)
     *              Sets the date picker month to first availability if any else defaults to current date
     *              Fetches the availabilities for the month set in previous line
     */
        async toMomentStep() {
            this.isFetchingFirstAvail = true;
            const filters = this.mapServiceFiltersAnswers();
            let dateStart = (this.delayTolerance.minTolerance ? this.delayTolerance.minTolerance : '');
            let dateStop = (this.delayTolerance.maxTolerance ? this.delayTolerance.maxTolerance : '');

            const allPlacesId = map(this.places, 'id');
            try {
                // Get the first availability
                let firstAvailability = null;

                if (!isNil(this.selectedService) && this.selectedService.id) {
                    firstAvailability = await this.getFirstAvailability([
                        this.selectedPlace.id === 0 ? allPlacesId : [this.selectedPlace.id],
                        this.selectedService.id,
                        filters,
                        this.clientTimeZone,
                        dateStart,
                        dateStop,
                    ]);
                }

                // START KPI Statistic
                let firstAvailabilityDate = null;
                let place = null;

                if (firstAvailability != null) {
                    firstAvailabilityDate = firstAvailability.start;
                }

                if (this.selectedPlace.id !== 0) {
                    place = this.selectedPlace.id;
                }

                if (get(this.selectedService, 'id')) {
                    await this.insertKPIStatistic([this.selectedService.id, place, firstAvailabilityDate]);
                }
                // END KPI Statistic

                if (!isNil(firstAvailability) && !this.isDateUpcomingEvent(format(firstAvailability.start, 'YYYY-MM-DD'))) {
                    this.start = format(firstAvailability.start, 'YYYY-MM-DD');
                }
                else {
                    this.start = format(new Date(), 'YYYY-MM-DD');
                }
                await this.fetchAvailabilitiesBetweenDates();

                if (this.getAvailabilities && this.getAvailabilities.length) {
                    this.initiallyNoAvails = false;
                }
            }
            finally {
                this.isFetchingFirstAvail = false;
            }
        },

        isOwnerAppointment() {
            return !some(this.getApptFormMetasReordered, { 'metadata': MetadataEnum.REQUESTER_LINK, 'value': 1 });
        },

        /**
     * @description Manually unselect the other selected time
     * @param id
     */
        unSelectOtherRadioTime(id) {
            forEach(this.selectedRadioGroupTime, function(value, key, obj) {
                if (key !== id) {
                    obj[key] = false;
                }
            });
        },

        /**
     * @description Show optinNewServiceList checkbox if clientData contains email
     */
        showOptInList() {
            return some(this.getApptFormMetasReordered, { 'metadata': MetadataEnum.EMAIL, 'status': 1 }) &&
          !find(this.apptForm.metas, ['metadata', MetadataEnum.VIRTUAL_ACCOUNT]);
        },

        /**
         * @description If you arrive from portal with a flu service and a selected place, go directly to moment step
         */
        async passDirectlyToMomentStep() {
            if (this.getSelectedPlaceId && this.getSelectedServiceId) {
                await this.$store.dispatch('loading/toggleLoading', { set: true });
                this.selectedPlace = find(this.getPlaces, ['id', parseInt(this.getSelectedPlaceId)]);
                if (this.selectedPlace) {
                    await this.toNextStep(findIndex(this.steps, ['name', 'moment']));
                    await this.$store.dispatch('loading/toggleLoading', { set: false });
                }
                else {
                    await this.toNextStep(this.getPlaceStep.num - 1);
                }
            }
        },

        /**
        * @description Close and save datePicker
        * @param {Date} date
        */
        savePregnancyDpa(date) {
            this.pregnancyDpa = date;
        },

        /**
        * @description update metadata object from metadataform component
        * @param metadata
        */
        async updateMetadata(metadata, fromProfil = false) {
            this.apptForm.clientData[this.getBaseMetadataById(metadata.metadata)['name']] = metadata.value;
            /**
             * Metadataform Vue Component use the getters named getCopySelectedServiceMetadataValuesOverrideApplied to
             * take decision for form inputs rendering.
             *
             * Since the feature that allow a custom display order for metadatas needed a change in the variable type
             * (from Object to Array), it was decided to update both entities instead of refactoring the MetadataForm Vue Component.
             */
            Vue.set(this.getCopySelectedServiceMetadataValuesOverrideApplied, metadata.metadata, metadata);
            Vue.set(this.apptForm.metas, findIndex(this.apptForm.metas, ['metadata', metadata.metadata]), metadata);
            if (metadata.metadata === MetadataEnum.PREGNANCY_DPA) {
                this.savePregnancyDpa(metadata.value);
            }

            if (fromProfil) {
                /**
                 * Prefill inputs procedure after selecting a Patient from an authenticated user take a reasonable amount of time to be updated in the input.
                 * Therefore, we need to wait for the next thick to re-evaluate which required fields are still empty.
                 */
                await this.$nextTick();
                this.isFormValid = get(this.$refs, 'appointmentForm[0]') ? this.$refs.appointmentForm[0].validate() : false;
            }
        },

        updatePatientUuid(uuid) {
            this.patientUuid = uuid;
        },

        /**
     * Reset recaptcha when expired
     */
        onCaptchaExpired() {
            this.recaptchaToken = null;
            if (this.$refs.bookApptRecaptcha) {
                this.$refs.bookApptRecaptcha[0].reset();
            }
        },

        handleBackToResults() {
            window.close();
        },

        updateStart(payload) {
            this.start = payload.date;
        },

        handleSignInClick(isRegister) {
            if (isUsingKc) {
                if (isRegister) {
                    this.openKcRegisterWindow();
                }
                else {
                    this.openKcSignInWindow();
                }
                return;
            }
            this.setLocaleForProfile(this.locale);
            this.openSignInWindow(isRegister);
        },

        handleTermsAndConditionsCheckbox(value) {
            this.tosAndGDPRAgreement = !!value;
        },
    },
};

</script>

<style lang="scss">
@import './src/assets/scss/variables';

#body-wrapper {
  width: 100%;
  max-width: 100%;
  @media only screen and (max-width: 960px) {
    .btn-container {
      display: flex;
      justify-content: space-evenly;
    }
  }
}

.place-time-loop label:first-child,
.time-loop label:first-child {
  font-weight: bold;
  padding: 0;
  border-bottom: 1px solid;
}

.time-chip label {
  color: #fff !important;
  left: 5px !important;
}

.time-chip {
  z-index: 1;
}

.time-chip .v-chip__content:hover {
  cursor: pointer;
}

.establishment-card {
  overflow: hidden;
}

.establishment-description,
.popup-text,
.service-description {
  border-radius: 5px;

  strong {
    font-weight: bold;
  }

  div {
    font-size: 16px;
  }

  p {
    padding: 5px 0;
    font-size: 16px;
  }

  ul, ol {
    padding: 5px 20px;
    font-size: 16px;
    list-style-position: inside;
  }

  .popup-title {
    display: initial;
    font-weight: bold;
    font-size: 120%;
  }
}

.service-metas {
  display: inline-block;
  width: 100%;
}

.service-metas h6 {
  float: left;
  width: 10%;
}

.hidden-md-and-up .service-metas h6 {
  float: left;
  width: 25%;
}

.service-metas span {
  float: left;
  width: auto;
}

.time-chip label {
  color: #fff !important;
}

.v-chip .v-input--selection-controls__input {
  display: none;
}

.v-date-picker-table__event {
  z-index: -1;
  bottom: 0;
  height: 30px;
  transform: translateX(-50%);
  width: 30px;
}

.txt-nbr input {
  text-align: center;
}

.v-slider__thumb-container:hover {
  cursor: pointer;
}

.map {
  border-radius: 16px;
  border-width: 1px;
  border-style: solid;
}

.v-stepper__step__step .fa-edit {
  font-size: 14px;
  margin-left: 3px;
}

#stepper-header .v-stepper__label span.title {
  color: #727272 !important;
}

.day-status-circle {
  border-radius: 50%;
  width: 10px;
  height: 10px;
}

#calendar {

  .v-calendar-weekly__day {
    padding: 0;
  }
}

.main-avails-background-kids {
  background-color: #aaf3d0 !important;
}

.tolerance-avails-background {
  background-color: #daf3f8 !important;
}

.stroke--text {
  text-decoration: line-through;
}

.v-calendar-weekly__day-label:hover {
  text-decoration: none;
}

.v-stepper__step--active + .v-stepper__content
.v-stepper__wrapper {
  height: auto !important
}

button.time-chip.v-btn.appt-time-primary {
  color: #6D6D6D;
  background-color: #FFFFFF;
}

.v-btn:not(.v-btn--round).v-size--default.small-button {
  min-width: 44px;
  padding: 0;
  margin-top: 5px;
  margin-left: 0;
}

.v-btn:not(.v-btn--round).v-size--default {
  height: 44px;
}

    .data-collection-form-mobile-app {
        margin: 0 !important;
        padding: 0 20px !important;
    }
</style>
